/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect,useCallback,useRef} from 'react';
import {useLocation} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Modal from '../components/common/Modal';
import {Select} from '../components/common/FormField';
import SearchableSelect from '../components/common/SearchableSelect';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import {useAuth} from '../hooks/useAuth';
import api from '../services/api';
import {fmtDate,fmtDateTime,calcAge,safeArray} from '../utils/helpers';
import {openPrintWindow} from '../utils/printUtils';
import TerminologySearch from '../components/common/TerminologySearch';
import DrugInteractionAlert from '../components/clinical/DrugInteractionAlert';

/* SPEECH-TO-TEXT HOOK */
function useSpeechToText(onResult:(text:string)=>void){
  const [listening,setListening]=useState(false);
  const recRef=useRef<any>(null);
  const toggle=()=>{
    const SR=(window as any).SpeechRecognition||(window as any).webkitSpeechRecognition;
    if(!SR){ alert('Speech recognition not supported in this browser. Use Chrome.'); return; }
    if(listening){ recRef.current?.stop(); setListening(false); return; }
    const rec=new SR();
    rec.lang='en-IN'; rec.continuous=true; rec.interimResults=true;
    let finalTranscript='';
    rec.onresult=(e:any)=>{
      let interim='';
      for(let i=e.resultIndex;i<e.results.length;i++){
        const t=e.results[i][0].transcript;
        if(e.results[i].isFinal) finalTranscript+=t+' ';
        else interim+=t;
      }
      onResult(finalTranscript+interim);
    };
    rec.onend=()=>setListening(false);
    rec.onerror=()=>setListening(false);
    rec.start(); recRef.current=rec; setListening(true);
  };
  return {listening,toggle};
}

function MicButton({onResult,baseText}:{onResult:(t:string)=>void;baseText:string}){
  const {listening,toggle}=useSpeechToText(t=>onResult((baseText?baseText+' ':'')+t));
  return(
    <button type="button" onClick={toggle}
      title={listening?'Stop dictation':'Start voice dictation'}
      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all
        ${listening?'bg-red-500 text-white animate-pulse shadow-lg':'bg-slate-100 text-slate-400 hover:bg-blue-100 hover:text-[#02357A]'}`}>
      🎤
    </button>
  );
}

function VoiceTextarea({value,onChange,placeholder,rows=3,label}:{value:string;onChange:(v:string)=>void;placeholder?:string;rows?:number;label?:string}){
  return(
    <div>
      {label&&<label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>}
      <div className="relative">
        <textarea className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 resize-y min-h-[80px] leading-relaxed pr-11" rows={rows} value={value}
          onChange={e=>onChange(e.target.value)} placeholder={placeholder}/>
        <div className="absolute right-2 top-2">
          <MicButton baseText={value} onResult={onChange}/>
        </div>
      </div>
    </div>
  );
}

function DrugSearchInput({onSelect,placeholder='Search drug from formulary...'}:{onSelect:(d:any)=>void;placeholder?:string}){
  const [q,setQ]=useState('');
  const [results,setResults]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [open,setOpen]=useState(false);
  const ref=useRef<HTMLDivElement>(null);

  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setOpen(false);};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);

  useEffect(()=>{
    if(q.length<2){setResults([]);setOpen(false);return;}
    const t=setTimeout(async()=>{
      setLoading(true);
      try{
        const r=await api.getDrugs({search:q,take:10});
        const list=Array.isArray(r.data?.drugs)?r.data.drugs:safeArray(r.data,['drugs']);
        setResults(list);setOpen(true);
      }catch{setResults([]);}finally{setLoading(false);}
    },300);
    return()=>clearTimeout(t);
  },[q]);

  return(
    <div ref={ref} className="relative">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">💊</span>
        <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 pl-10 pr-10" value={q} onChange={e=>setQ(e.target.value)}
          onFocus={()=>q.length>=2&&setOpen(true)} placeholder={placeholder} autoComplete="off"/>
        {loading&&<div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#02357A]/20 border-t-[#02357A] rounded-full animate-spin"/>}
      </div>
      {open&&results.length>0&&(
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden">
          <div className="max-h-56 overflow-y-auto">
            {results.map((d:any)=>(
              <div key={d.id} onClick={()=>{onSelect(d);setQ('');setOpen(false);}}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-green-50 cursor-pointer border-b border-slate-50 last:border-0">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-[#02357A] flex items-center justify-center text-xs font-bold flex-shrink-0">💊</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm">{d.name||d.drugName}</p>
                  <div className="flex gap-2 mt-0.5">
                    <span className="text-xs text-slate-400">{d.genericName||d.generic_name}</span>
                    <span className="text-xs text-[#02357A]">{d.dosageForm||d.dosage_form}</span>
                    {d.strength&&<span className="text-xs text-slate-500">{d.strength}</span>}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0
                  ${(d.totalStock||d.total_stock)>10?'bg-green-100 text-green-700':(d.totalStock||d.total_stock)>0?'bg-amber-100 text-amber-700':'bg-red-100 text-red-700'}`}>
                  Stock: {d.totalStock||d.total_stock||0}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {open&&q.length>=2&&!loading&&results.length===0&&(
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-xl p-3 text-center text-sm text-slate-500">
          No drugs found
        </div>
      )}
    </div>
  );
}

const TABS=[
  ['overview','📋 Overview'],
  ['history','🕐 Visit History'],
  ['profile','🩺 Clinical Profile'],
  ['vitals','❤️ Vitals'],
  ['consultation','📝 Consultation & Diagnosis'],
  ['results','🔬 Investigation Results'],
  ['diagnoses','🔬 Diagnoses'],
  ['chronic','🔄 Chronic Conditions'],
  ['careplan','🗂️ Care Plans'],
  ['alerts','⚠️ Clinical Alerts'],
  ['cpoe','💊 CPOE Orders'],
  ['referral','↗️ Referrals'],
  ['documents','📁 Documents'],
  ['prescription','🖨️ Prescription'],
  ['discharge','📄 Discharge Summary'],
  ['immunization','💉 Immunizations'],
] as const;

const SEVERITY_META:Record<string,string>={
  MILD:'bg-yellow-100 text-yellow-700',
  MODERATE:'bg-orange-100 text-orange-700',
  SEVERE:'bg-red-100 text-red-700',
  LIFE_THREATENING:'bg-red-600 text-white',
};

const ORDER_TYPES=['PHARMACY','LAB','RADIOLOGY','PROCEDURE','DIET','NURSING'];
const PRIORITIES=['ROUTINE','URGENT','STAT','EMERGENCY'];
const PRIORITY_META:Record<string,string>={
  ROUTINE:'bg-slate-100 text-slate-600',
  URGENT:'bg-amber-100 text-amber-700',
  STAT:'bg-red-100 text-red-700',
  EMERGENCY:'bg-red-600 text-white',
};

export default function DoctorEMRPage(){
  const {user}=useAuth();
  const location=useLocation();
  const [tab,setTab]=useState<string>('overview');

  const [selectedPatient,setSelectedPatient]=useState<any>(null);
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [labTests,setLabTests]=useState<any[]>([]);
  const [radServices,setRadServices]=useState<any[]>([]);
  const [procedures,setProcedures]=useState<any[]>([]);
  const [dietMaster,setDietMaster]=useState<any[]>([]);
  const NURSING_TASKS=[
    {value:'Strict Bed Rest',label:'Strict Bed Rest',badge:'RESTRICTION'},
    {value:'Bed Rest with Bathroom Privileges',label:'Bed Rest with Bathroom Privileges',badge:'RESTRICTION'},
    {value:'Vital Signs Q1H',label:'Vital Signs Every 1 Hour',badge:'MONITORING'},
    {value:'Vital Signs Q2H',label:'Vital Signs Every 2 Hours',badge:'MONITORING'},
    {value:'Vital Signs Q4H',label:'Vital Signs Every 4 Hours',badge:'MONITORING'},
    {value:'Vital Signs Q6H',label:'Vital Signs Every 6 Hours',badge:'MONITORING'},
    {value:'Vital Signs Q8H',label:'Vital Signs Every 8 Hours',badge:'MONITORING'},
    {value:'Vitals QID',label:'Vital Signs 4 Times Daily',badge:'MONITORING'},
    {value:'SpO2 Continuous Monitoring',label:'SpO2 Continuous Monitoring',badge:'MONITORING'},
    {value:'Blood Sugar Monitoring QID',label:'Blood Sugar Monitoring QID',badge:'MONITORING'},
    {value:'Input Output Chart',label:'Strict Input/Output Chart',badge:'MONITORING'},
    {value:'Daily Weight',label:'Daily Weight',badge:'MONITORING'},
    {value:'Urine Output Hourly',label:'Urine Output Hourly',badge:'MONITORING'},
    {value:'Wound Dressing Daily',label:'Wound Dressing Daily',badge:'CARE'},
    {value:'Wound Dressing BD',label:'Wound Dressing Twice Daily',badge:'CARE'},
    {value:'Catheter Care',label:'Catheter Care',badge:'CARE'},
    {value:'Nasogastric Tube Care',label:'NG Tube Care',badge:'CARE'},
    {value:'Oral Hygiene',label:'Oral Hygiene Twice Daily',badge:'HYGIENE'},
    {value:'Sponge Bath',label:'Sponge Bath Daily',badge:'HYGIENE'},
    {value:'Pressure Sore Care',label:'Pressure Sore Prevention/Care',badge:'CARE'},
    {value:'Position Change Q2H',label:'Position Change Every 2 Hours',badge:'POSITIONING'},
    {value:'Head End Elevation 30 Degrees',label:'Head End Elevation 30°',badge:'POSITIONING'},
    {value:'Fall Precautions',label:'Fall Precautions',badge:'CARE'},
    {value:'Deep Breathing Exercises',label:'Deep Breathing Exercises',badge:'CARE'},
    {value:'Incentive Spirometry',label:'Incentive Spirometry Hourly',badge:'CARE'},
    {value:'Cold/Hot Fomentation',label:'Cold/Hot Fomentation',badge:'CARE'},
    {value:'Enema',label:'Enema',badge:'CARE'},
    {value:'Blood Sample Collection',label:'Blood Sample Collection',badge:'SPECIMEN'},
    {value:'Urine Sample Collection',label:'Urine Sample Collection',badge:'SPECIMEN'},
  ];
  const [saving,setSaving]=useState(false);

  const [visitHistory,setVisitHistory]=useState<any[]>([]);
  const [profile,setProfile]=useState<any>(null);
  const [vitals,setVitals]=useState<any[]>([]);
  const [cpoeOrders,setCpoeOrders]=useState<any[]>([]);
  const [referrals,setReferrals]=useState<any[]>([]);
  const [documents,setDocuments]=useState<any[]>([]);
  const [prescriptions,setPrescriptions]=useState<any[]>([]);
  const [doctors,setDoctors]=useState<any[]>([]);

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const loadAll=useCallback(async(pid:string)=>{
    setLoading(true);
    try{
      const [vh,pr,vt,cp,rf,doc,rx, al, gc]=await Promise.all([
        api.getVisitHistory(pid).catch(()=>({data:[]})),
        api.getClinicalProfile(pid).catch(()=>({data:null})),
        api.getOPDVitals?.(pid).catch(()=>api.getVitals(pid)).catch(()=>({data:[]})),
        api.getCPOEOrders({patientId:pid}).catch(()=>({data:[]})),
        api.getReferrals({patientId:pid}).catch(()=>({data:[]})),
        api.getClinicalDocuments(pid).catch(()=>({data:[]})),
        api.getPrescriptions(pid).catch(()=>({data:[]})),
        api.getAllergiesById?.(pid).catch(()=>({data:[]})),
        api.getConsultations({patientId:pid,take:5})
      ]);
          setVisitHistory(Array.isArray(vh.data)?vh.data:safeArray(vh.data));
      const allergiesList=al?.data?.allergies||al?.data?.data||(Array.isArray(al?.data)?al.data:[]);
      const profileData=pr.data||null;
      if(profileData&&allergiesList.length>0) profileData.allergies=allergiesList;
      else if(profileData&&!profileData.allergies) profileData.allergies=allergiesList;
      setProfile(profileData);
      // setProfile(pr.data||null);
      setVitals(Array.isArray(vt.data)?vt.data:safeArray(vt.data));
      setCpoeOrders(Array.isArray(cp.data)?cp.data:safeArray(cp.data));
      setReferrals(Array.isArray(rf.data)?rf.data:safeArray(rf.data));
      setDocuments(Array.isArray(doc.data)?doc.data:safeArray(doc.data));
      setPrescriptions(Array.isArray(rx.data)?rx.data:safeArray(rx.data));
    }catch{}finally{setLoading(false);}
  },[]);

  useEffect(()=>{
    api.getDoctors().then(r=>setDoctors(Array.isArray(r.data)?r.data:safeArray(r.data,['users']))).catch(()=>{});
    api.getLabTests().then(r=>setLabTests(r.data||[])).catch(()=>{});
    api.getRadiologyServices().then(r=>setRadServices(r.data||[])).catch(()=>{});
    api.getProcedureCatalog().then(r=>setProcedures(r.data||[])).catch(()=>{});
    api.getDietTypes().then(r=>setDietMaster(r.data||[])).catch(()=>{});
  },[]);

  useEffect(()=>{
    const pid=(location.state as any)?.patientId;
    if(pid&&!selectedPatient){
      api.getPatient(pid).then(r=>{
        const p=r.data;
        if(p){ setSelectedPatient(p); loadAll(p.id); }
      }).catch(()=>{});
    }
  },[location.state]);

  const onPatientSelect=(p:any)=>{
    setSelectedPatient(p);
    setTab('overview');
    loadAll(p.id);
  };
  const onPatientClear=()=>{
    setSelectedPatient(null);
    setVisitHistory([]);setProfile(null);setVitals([]);
    setCpoeOrders([]);setReferrals([]);setDocuments([]);setPrescriptions([]);
  };

  const allergies=profile?.allergies||[];
  const latestVitals=vitals[0];

  return(
    <Layout title="EMR Workspace">
      {msg&&(
        <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold border flex items-center gap-2 ${msg.startsWith('✅')?'bg-green-50 text-green-800 border-green-200':msg.startsWith('⚠️')?'bg-amber-50 text-amber-800 border-amber-200':'bg-red-50 text-red-800 border-red-200'}`}>
          {msg}
        </div>
      )}

      <div className="card p-5 mb-5 rounded-2xl">
        <PatientSearchInput
          label="Patient"
          selected={selectedPatient}
          onSelect={onPatientSelect}
          onClear={onPatientClear}
          placeholder="Search patient by name, UHID or phone to open their EMR..."
        />
      </div>

      {!selectedPatient?(
        <div className="card p-16 text-center">
          <span className="text-5xl block mb-3">🩺</span>
          <p className="font-semibold text-slate-600">Search for a patient to open their EMR</p>
          <p className="text-slate-400 text-sm mt-1">Visit history, clinical profile, CPOE, prescriptions and more</p>
        </div>
      ):(
        <>
          <div className="card p-5 mb-5 rounded-2xl bg-gradient-to-r from-blue-50 to-white border-blue-100">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#02357A] to-[#039089] text-white flex items-center justify-center font-black text-xl flex-shrink-0 shadow-lg">
                  {(selectedPatient.firstName||selectedPatient.first_name||'P').charAt(0)}
                </div>
                <div>
                  <p className="font-black text-slate-800 text-lg">
                    {(selectedPatient.firstName||selectedPatient.first_name)+' '+(selectedPatient.lastName||selectedPatient.last_name)}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <span className="font-mono text-xs text-[#02357A] bg-blue-100 px-2 py-0.5 rounded">{selectedPatient.uhid}</span>
                    {(selectedPatient.dateOfBirth||selectedPatient.date_of_birth)&&
                      <span className="text-xs text-slate-500">{calcAge(selectedPatient.dateOfBirth||selectedPatient.date_of_birth)}</span>}
                    {selectedPatient.gender&&<span className="text-xs text-slate-500">{selectedPatient.gender}</span>}
                    {(selectedPatient.bloodGroup||selectedPatient.blood_group)&&
                      <span className="text-xs font-bold text-red-600">🩸 {selectedPatient.bloodGroup||selectedPatient.blood_group}</span>}
                    {selectedPatient.phone&&<span className="text-xs text-slate-400">📱 {selectedPatient.phone}</span>}
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-[200px]">
                {allergies.length>0?(
                  <div className="bg-red-50 border-2 border-red-300 rounded-xl p-2.5">
                    <p className="text-xs font-black text-red-700 mb-1">⚠️ ALLERGIES ({allergies.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {allergies.map((a:any,i:number)=>(
                        <span key={i} className={`text-xs font-bold px-2 py-0.5 rounded-full ${SEVERITY_META[a.severity]||'bg-red-100 text-red-700'}`}>
                          {a.allergen} ({a.severity})
                        </span>
                      ))}
                    </div>
                  </div>
                ):(selectedPatient.knownAllergies||selectedPatient.known_allergies)?(
                  <div className="bg-red-50 border-2 border-red-300 rounded-xl p-2.5">
                    <p className="text-xs font-black text-red-700">⚠️ {selectedPatient.knownAllergies||selectedPatient.known_allergies}</p>
                  </div>
                ):(
                  <div className="bg-green-50 border border-green-200 rounded-xl p-2.5 text-center">
                    <p className="text-xs font-semibold text-green-600">✅ No known allergies recorded</p>
                  </div>
                )}
              </div>

              {latestVitals&&(
                <div className="grid grid-cols-3 gap-1.5 flex-shrink-0">
                  {[
                    ['BP',`${latestVitals.systolicBp||'—'}/${latestVitals.diastolicBp||'—'}`],
                    ['HR',latestVitals.heartRate],
                    ['SpO₂',latestVitals.oxygenSaturation],
                  ].map(([l,v])=>(
                    <div key={l} className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-center">
                      <p className="text-xs text-slate-400">{l}</p>
                      <p className="font-bold text-sm text-slate-700">{v||'—'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-1.5 mb-4 flex-wrap overflow-x-auto pb-1">
            {TABS.map(([t,l])=>(
              <button key={t} onClick={()=>setTab(t)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap border-2 flex-shrink-0
                  ${tab===t?'bg-[#02357A] text-white border-[#02357A] shadow-lg shadow-[#02357A]/20':'bg-white text-slate-600 border-slate-200 hover:border-[#02357A] hover:text-[#02357A]'}`}>
                {l}
              </button>
            ))}
          </div>

          {loading?(
            <div className="card p-14 text-center rounded-2xl"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
          ):(
            <EMRTabContent
              tab={tab} setTab={setTab}
              patient={selectedPatient} doctors={doctors} user={user}
              visitHistory={visitHistory} profile={profile} setProfile={setProfile}
              vitals={vitals} setVitals={setVitals}
              cpoeOrders={cpoeOrders} setCpoeOrders={setCpoeOrders}
              referrals={referrals} setReferrals={setReferrals}
              documents={documents} setDocuments={setDocuments}
              prescriptions={prescriptions} setPrescriptions={setPrescriptions}
              saving={saving} setSaving={setSaving} notify={notify}
              reload={()=>loadAll(selectedPatient.id)}
              labTests={labTests} radServices={radServices}
              procedures={procedures} dietMaster={dietMaster}
              NURSING_TASKS={NURSING_TASKS}
            />
          )}
        </>
      )}
    </Layout>
  );
}

function EMRTabContent(props:any){
  const {tab}=props;
  switch(tab){
    case 'overview':     return <OverviewTab {...props}/>;
    case 'history':      return <VisitHistoryTab {...props}/>;
    case 'profile':       return <ClinicalProfileTab {...props}/>;
    case 'vitals':        return <VitalsTab {...props}/>;
    case 'consultation':  return <ConsultationTab {...props}/>;
    case 'results':       return <InvestigationResultsTab {...props}/>;
    case 'diagnoses':     return <DiagnosesTab {...props}/>;
    case 'chronic':       return <ChronicConditionsTab {...props}/>;
    case 'careplan':      return <CarePlansTab {...props}/>;
    case 'alerts':        return <ClinicalAlertsTab {...props}/>;
    case 'cpoe':          return <CPOETab {...props}/>;
    case 'referral':      return <ReferralTab {...props}/>;
    case 'documents':     return <DocumentsTab {...props}/>;
    case 'prescription':  return <PrescriptionTab {...props}/>;
    case 'discharge':     return <DischargeSummaryTab {...props}/>;
    case 'immunization':  return <ImmunizationTab {...props}/>;
    default: return null;
  }
}

function OverviewTab({patient,profile,vitals,cpoeOrders,referrals,visitHistory,documents,setTab}:any){
  const latestVitals=vitals[0];
  const activeAllergies=profile?.allergies||[];
  const pendingOrders=cpoeOrders.filter((o:any)=>o.status==='ORDERED'||o.status==='IN_PROGRESS');

  return(
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <div className="card p-5 rounded-2xl">
          <h3 className="font-semibold text-slate-700 mb-3 text-sm">🩺 Clinical Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-400 uppercase mb-1">Comorbidities</p>
              <p className="text-slate-700">{profile?.comorbidities||'None recorded'}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-400 uppercase mb-1">Family History</p>
              <p className="text-slate-700">{profile?.familyHistory||'None recorded'}</p>
            </div>
          </div>
          {profile?.socialHabits&&Object.values(profile.socialHabits).some((v:any)=>v)&&(
            <div className="mt-3 bg-slate-50 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-400 uppercase mb-2">Social Habits</p>
              <div className="flex flex-wrap gap-2">
                {profile.socialHabits.smoking&&<span className="text-xs bg-white border px-2 py-1 rounded-full">🚬 Smoking: {profile.socialHabits.smoking}</span>}
                {profile.socialHabits.alcohol&&<span className="text-xs bg-white border px-2 py-1 rounded-full">🍷 Alcohol: {profile.socialHabits.alcohol}</span>}
                {profile.socialHabits.tobacco&&<span className="text-xs bg-white border px-2 py-1 rounded-full">🌿 Tobacco: {profile.socialHabits.tobacco}</span>}
                {profile.socialHabits.exercise&&<span className="text-xs bg-white border px-2 py-1 rounded-full">🏃 Exercise: {profile.socialHabits.exercise}</span>}
                {profile.socialHabits.diet&&<span className="text-xs bg-white border px-2 py-1 rounded-full">🍽️ Diet: {profile.socialHabits.diet}</span>}
              </div>
            </div>
          )}
        </div>

        <div className="card p-5 rounded-2xl">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-slate-700 text-sm">🕐 Recent Visits</h3>
            <button onClick={()=>setTab('history')} className="text-xs text-[#02357A] hover:underline">View all →</button>
          </div>
          {visitHistory.length===0?(
            <p className="text-slate-400 text-sm text-center py-4">No visit history</p>
          ):(
            <div className="space-y-2">
              {visitHistory.slice(0,4).map((v:any,i:number)=>(
                <div key={i} className="flex items-start gap-3 p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 mt-0.5
                    ${v.visit_type==='OPD'?'bg-blue-100 text-[#02357A]':
                      v.visit_type==='IPD'?'bg-purple-100 text-purple-700':
                      v.visit_type==='EMERGENCY'?'bg-red-100 text-red-700':
                      'bg-amber-100 text-amber-700'}`}>{v.visit_type}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 truncate font-medium">{v.chief_complaint||v.final_diagnosis||'—'}</p>
                    {v.doctor_name&&<p className="text-xs text-slate-400">Dr. {v.doctor_name} {v.doctor_last_name||''}</p>}
                    {v.final_diagnosis&&v.chief_complaint&&<p className="text-xs text-slate-500 truncate">Dx: {v.final_diagnosis}</p>}
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0">{fmtDate(v.visit_date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="card p-5 rounded-2xl">
          <h3 className="font-semibold text-slate-700 mb-3 text-sm">❤️ Latest Vitals</h3>
          {latestVitals?(
            <div className="grid grid-cols-2 gap-2">
              {[
                ['Temp',latestVitals.temperature,'°C'],
                ['BP',`${latestVitals.systolicBp||'—'}/${latestVitals.diastolicBp||'—'}`,''],
                ['HR',latestVitals.heartRate,'/min'],
                ['SpO₂',latestVitals.oxygenSaturation,'%'],
                ['Weight',latestVitals.weight,'kg'],
                ['BMI',latestVitals.bmi,''],
              ].map(([l,v,u])=>(
                <div key={l as string} className="bg-slate-50 rounded-lg p-2 text-center">
                  <p className="text-xs text-slate-400">{l}</p>
                  <p className="font-bold text-slate-700 text-sm">{v||'—'}{v&&u?` ${u}`:''}</p>
                </div>
              ))}
              <p className="col-span-2 text-xs text-slate-400 text-center mt-1">
                Recorded: {fmtDateTime(latestVitals.recordedAt||latestVitals.recorded_at)}
              </p>
            </div>
          ):<p className="text-slate-400 text-sm text-center py-4">No vitals recorded</p>}
        </div>

        <div className="card p-5 rounded-2xl">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-slate-700 text-sm">💊 Pending Orders</h3>
            <button onClick={()=>setTab('cpoe')} className="text-xs text-[#02357A] hover:underline">View all →</button>
          </div>
          {pendingOrders.length===0?(
            <p className="text-slate-400 text-sm text-center py-4">No pending orders</p>
          ):(
            <div className="space-y-1.5">
              {pendingOrders.slice(0,5).map((o:any,i:number)=>(
                <div key={i} className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded-lg">
                  <span className="text-slate-700">{o.itemName||o.item_name}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-bold ${PRIORITY_META[o.priority]||'bg-slate-100'}`}>{o.orderType||o.order_type}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card p-5 rounded-2xl">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-slate-700 text-sm">📁 Documents</h3>
            <button onClick={()=>setTab('documents')} className="text-xs text-[#02357A] hover:underline">View all →</button>
          </div>
          <p className="text-2xl font-black text-slate-700 mt-2">{documents.length}</p>
          <p className="text-xs text-slate-400">uploaded documents</p>
        </div>
      </div>
    </div>
  );
}


function VisitHistoryTab({visitHistory}:any){
  const [expandedId,setExpandedId]=React.useState<string|null>(null);
  const [filterType,setFilterType]=React.useState('');
  const TYPE_META:Record<string,{icon:string;bg:string;border:string;badge:string}>={
    OPD:      {icon:'🏥',bg:'bg-blue-50',   border:'border-blue-300',  badge:'bg-[#02357A] text-white'},
    IPD:      {icon:'🛏️',bg:'bg-purple-50', border:'border-purple-300',badge:'bg-purple-600 text-white'},
    EMERGENCY:{icon:'🚨',bg:'bg-red-50',    border:'border-red-300',   badge:'bg-red-600 text-white'},
    SURGERY:  {icon:'🔪',bg:'bg-amber-50',  border:'border-amber-300', badge:'bg-amber-600 text-white'},
  };
  const sorted=[...visitHistory].sort((a,b)=>new Date(b.visit_date).getTime()-new Date(a.visit_date).getTime());
  const filtered=filterType?sorted.filter(v=>v.visit_type===filterType):sorted;

  // Group by year-month
  const grouped:Record<string,any[]>={};
  filtered.forEach(v=>{
    const key=v.visit_date?new Date(v.visit_date).toLocaleString('en-IN',{year:'numeric',month:'long'}):'Unknown Date';
    if(!grouped[key]) grouped[key]=[];
    grouped[key].push(v);
  });

  return(
    <div className="space-y-4">
      {/* Filter bar — always shown */}
      <div className="card p-4 flex gap-4 items-center flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {['OPD','IPD','EMERGENCY','SURGERY'].map(t=>{
            const cnt=visitHistory.filter((v:any)=>v.visit_type===t).length;
            const m=TYPE_META[t];
            return(
              <button key={t} onClick={()=>cnt>0&&setFilterType(filterType===t?'':t)}
                disabled={cnt===0}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
                  ${cnt===0?'opacity-30 cursor-not-allowed bg-slate-50 border-slate-200 text-slate-400':
                    filterType===t?m.badge+' border-transparent':m.bg+' '+m.border}`}>
                {m.icon} {t} <span className="bg-white/30 rounded-full px-1.5">{cnt}</span>
              </button>
            );
          })}
        </div>
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} encounter(s){filterType?` — ${filterType} only`:''}</span>
      </div>

      {visitHistory.length===0?(
        <div className="card py-14 text-center rounded-2xl">
          <span className="text-4xl block mb-3">🕐</span>
          <p className="font-semibold text-slate-600 mb-1">No encounters recorded yet</p>
          <p className="text-sm text-slate-400 mb-4">Encounters are created when you save a consultation in OPD, admit the patient in IPD, or register in Emergency.</p>
          <div className="flex gap-3 justify-center">
            <a href="/opd" className="text-xs bg-[#02357A] text-white px-4 py-2 rounded-lg hover:bg-[#015CA6]">Go to OPD →</a>
            <a href="/ipd" className="text-xs bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">Go to IPD →</a>
          </div>
        </div>
      ):(
        <div className="space-y-6">
          {Object.entries(grouped).map(([month,visits])=>(
            <div key={month}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{month}</span>
                <div className="flex-1 h-px bg-slate-200"/>
                <span className="text-xs text-slate-400">{visits.length} encounter(s)</span>
              </div>
              <div className="space-y-3">
                {visits.map((v:any,i:number)=>{
                  const m=TYPE_META[v.visit_type]||{icon:'📄',bg:'bg-slate-50',border:'border-slate-200',badge:'bg-slate-600 text-white'};
                  const isExpanded=expandedId===v.id;
                  return(
                    <div key={i} className={`border-2 rounded-xl overflow-hidden transition-all ${m.border}`}>
                      {/* Encounter header — always visible */}
                      <button type="button" className={`w-full text-left p-4 ${m.bg} hover:brightness-[0.98] transition-all`}
                        onClick={()=>setExpandedId(isExpanded?null:v.id)}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${m.badge}`}>{m.icon} {v.visit_type}</span>
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800 text-sm truncate">
                                {v.chief_complaint||v.surgery_name||'No chief complaint recorded'}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {v.doctor_name?(v.doctor_name.startsWith('Dr.')?`${v.doctor_name} ${v.doctor_last_name||''}`:`Dr. ${v.doctor_name} ${v.doctor_last_name||''}`):''}
                                {v.doctor_name&&v.ipd_number?' · ':''}
                                {v.ipd_number?`IPD No: ${v.ipd_number}`:''}
                                {v.triage_level?`Triage: ${v.triage_level}`:''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(v.visit_date)}</span>
                            {v.status&&<span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${v.status==='COMPLETED'?'bg-green-100 text-green-700':v.status==='ACTIVE'||v.status==='ONGOING'?'bg-blue-100 text-[#02357A]':'bg-slate-100 text-slate-500'}`}>{v.status}</span>}
                            <span className="text-slate-400 text-xs">{isExpanded?'▲':'▼'}</span>
                          </div>
                        </div>
                      </button>

                      {/* Expanded clinical detail */}
                      {isExpanded&&(
                        <div className="bg-white p-4 border-t border-slate-100 grid grid-cols-2 gap-4 text-sm">
                          {v.final_diagnosis&&(
                            <div className="col-span-2">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Final Diagnosis</p>
                              <p className="font-semibold text-slate-800">{v.final_diagnosis} {v.icd_code?<span className="text-xs font-mono text-slate-400 ml-1">({v.icd_code})</span>:''}</p>
                            </div>
                          )}
                          {v.advice&&(
                            <div className="col-span-2">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Advice / Treatment Plan</p>
                              <p className="text-slate-700">{v.advice}</p>
                            </div>
                          )}
                          {v.discharge_type&&(
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Discharge Type</p>
                              <p className="text-slate-700">{v.discharge_type}</p>
                            </div>
                          )}
                          {v.discharge_date&&(
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Discharge Date</p>
                              <p className="text-slate-700">{fmtDate(v.discharge_date)}</p>
                            </div>
                          )}
                          {v.doctor_name&&(
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Attending Doctor</p>
                              <p className="text-slate-700">Dr. {v.doctor_name} {v.doctor_last_name||''}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Encounter ID</p>
                            <p className="text-xs font-mono text-slate-400">{v.id?.slice(0,16)}…</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClinicalProfileTab({patient,profile,setProfile,saving,setSaving,notify,user}:any){
  const [showAllergy,setShowAllergy]=React.useState(false);
  const [allergyForm,setAllergyForm]=React.useState({allergen:'',allergyType:'DRUG',severity:'MODERATE',reaction:'',notes:'',
    encounterId:'',
    encounterNumber:'',
    encounterType:'OPD',
    encounterDate:'',
    presentingComplaint:'',
    durationOfComplaints:'',
    historyPresentIllness:'',
    pastMedicalHistory:'',
    pastSurgicalHistory:'',
    familyHistory:'',
    socialHistory:'',
    allergyHistory:'',
    drugHistory:'',
    reviewOfSystems:'',
    systemic:'',
    local:'',
    generalExam:'',
    provisionalDiagnosis:'',
    differentialDiagnosis:'',
    diagnosisIcd10:'',
    diagnosisSnomed:'',
    diagnosisConfirmed:false,
    diagnosisType:'PRIMARY',
    comorbidities:'',
    soapSubjective:'',
    soapObjective:'',
    soapAssessment:'',
    soapPlan:'',
    investigationOrdered:'',
    procedureOrdered:'',
    sickLeaveRecommended:false,
    sickLeaveDays:'',
    followUpRequired:true,
    followUpDays:'7',
    followUpReason:'',
    referralRequired:false,
    referralTo:'',
    referralReason:'',
    revisitAppointmentId:'',
    counsellingDone:false,
    counsellingNotes:'',
    clinicalSummary:'',
    progressNotes:''
  });
  const [form,setForm]=React.useState({
    comorbidities:   profile?.comorbidities||'',
    familyHistory:   profile?.familyHistory||'',
    tobaccoUse:      profile?.socialHabits?.tobacco||'',
    alcoholUse:      profile?.socialHabits?.alcohol||'',
    drugUse:         profile?.socialHabits?.drug||'',
    socialHistory:   profile?.socialHabits?.socialHistory||'',
    surgicalHistory: profile?.socialHabits?.surgicalHistory||'',
  });
  // const [form,setForm]=React.useState({familyHistory:profile?.familyHistory||'',comorbidities:profile?.comorbidities||'',surgicalHistory:profile?.surgicalHistory||'',socialHistory:profile?.socialHistory||'',tobaccoUse:profile?.tobaccoUse||'',alcoholUse:profile?.alcoholUse||'',drugUse:profile?.drugUse||''});
  const [allergies,setAllergies]=React.useState<any[]>(profile?.allergies||[]);
  const vitals=profile?.vitals||[];
  const latestV=vitals[0];


  React.useEffect(()=>{
    if(!profile)return;
    setForm({
      comorbidities:   profile.comorbidities||'',
      familyHistory:   profile.familyHistory||'',
      tobaccoUse:      profile.socialHabits?.tobacco||'',
      alcoholUse:      profile.socialHabits?.alcohol||'',
      drugUse:         profile.socialHabits?.drug||'',
      socialHistory:   profile.socialHabits?.socialHistory||'',
      surgicalHistory: profile.socialHabits?.surgicalHistory||'',
    });
    setAllergies(profile.allergies||[]);
  },[profile]);

  const saveProfile=async()=>{
    if(!patient?.id)return;setSaving(true);
    try{
      await api.updateClinicalProfile(patient.id,{
        comorbidities: form.comorbidities,
        familyHistory: form.familyHistory,
        socialHabits:{
          tobacco:form.tobaccoUse,
          alcohol:form.alcoholUse,
          drug:form.drugUse,
          socialHistory:form.socialHistory,
          surgicalHistory:form.surgicalHistory,
        },
      });
      notify('✅ Clinical profile saved!');
      setProfile((p:any)=>({...p,...form,socialHabits:{...(p?.socialHabits||{}),tobacco:form.tobaccoUse,alcohol:form.alcoholUse,drug:form.drugUse,socialHistory:form.socialHistory,surgicalHistory:form.surgicalHistory}}));
    }
    catch{notify('❌ Error saving profile');}
    setSaving(false);
  };

  const addAllergy=async()=>{
    if(!allergyForm.allergen){notify('Enter allergen name');return;}
    setSaving(true);
    try{
      await api.addAllergy({patientId:patient?.id,allergen:allergyForm.allergen,type:allergyForm.allergyType,allergyType:allergyForm.allergyType,severity:allergyForm.severity,reaction:allergyForm.reaction,notes:allergyForm.notes});
      const r=await api.getAllergiesById?.(patient?.id);
      const fresh=r?.data?.allergies||r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
      const updated=fresh.length>0?fresh:[...allergies,allergyForm];
      setAllergies(updated);
      setProfile((p:any)=>({...p,allergies:updated}));
      notify('✅ Allergy added!');setShowAllergy(false);
    }catch(e:any){notify('❌ '+(e?.response?.data?.message||'Error'));}
    setSaving(false);
  };

  return(
    <div className="space-y-4">
      {/* Latest Vitals Quick View */}
      {latestV&&(
        <div className="card p-5 rounded-2xl bg-blue-50 border-blue-200">
          <p className="text-xs font-semibold text-[#02357A] mb-2">Latest Vitals</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-center text-sm">
            {latestV.systolicBP&&<div><p className="font-bold text-[#02357A]">{latestV.systolicBP}/{latestV.diastolicBP}</p><p className="text-xs text-slate-400">BP (mmHg)</p></div>}
            {latestV.heartRate&&<div><p className="font-bold">{latestV.heartRate}</p><p className="text-xs text-slate-400">HR (bpm)</p></div>}
            {latestV.temperature&&<div><p className="font-bold">{latestV.temperature}°F</p><p className="text-xs text-slate-400">Temp</p></div>}
            {latestV.oxygenSaturation&&<div><p className="font-bold">{latestV.oxygenSaturation}%</p><p className="text-xs text-slate-400">SpO₂</p></div>}
          </div>
        </div>
      )}

      {/* Allergies */}
      <div className="card p-5 rounded-2xl">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-slate-700">⚠️ Allergies</h3>
          <button onClick={()=>setShowAllergy(!showAllergy)} className="text-xs btn-secondary py-1">+ Add Allergy</button>
        </div>
        {allergies.length===0
          ?<div className="text-center py-3"><p className="text-sm font-medium text-emerald-600">✅ No known allergies recorded</p></div>
          :<div className="flex flex-wrap gap-2">
            {allergies.map((a:any,i:number)=>{
              const sc:Record<string,string>={MILD:'bg-yellow-50 border-yellow-200 text-yellow-700',MODERATE:'bg-orange-50 border-orange-200 text-orange-700',SEVERE:'bg-red-100 border-red-300 text-red-700',LIFE_THREATENING:'bg-red-600 border-red-700 text-white'};
              return<span key={i} className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl border ${sc[a.severity]||'bg-red-50 border-red-200 text-red-700'}`}>⚠️ {a.allergen||a.allergenName}<span className="opacity-70 font-normal">({a.type||a.allergyType}) — {a.severity}</span></span>;
            })}
          </div>}
        {showAllergy&&(
          <div className="mt-3 grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-xl">
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Allergen *</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={allergyForm.allergen} onChange={e=>setAllergyForm({...allergyForm,allergen:e.target.value})} placeholder="e.g. Penicillin, Peanuts"/></div>
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Type</label><select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={allergyForm.allergyType} onChange={e=>setAllergyForm({...allergyForm,allergyType:e.target.value})}><option>DRUG</option><option>FOOD</option><option>ENVIRONMENTAL</option><option>OTHER</option></select></div>
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Severity</label><select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={allergyForm.severity} onChange={e=>setAllergyForm({...allergyForm,severity:e.target.value})}><option>MILD</option><option>MODERATE</option><option>SEVERE</option><option>LIFE_THREATENING</option></select></div>
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Reaction</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={allergyForm.reaction} onChange={e=>setAllergyForm({...allergyForm,reaction:e.target.value})} placeholder="e.g. Rash, Anaphylaxis"/></div>
            <div className="col-span-2 flex gap-2 pt-1">
              <button onClick={addAllergy} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-xs flex-1">{saving?'Saving...':'Add'}</button>
              <button onClick={()=>setShowAllergy(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Clinical History Form */}
      <div className="card p-5 space-y-4 rounded-2xl">
        <h3 className="font-semibold text-slate-700 border-b pb-2">📋 Clinical History</h3>
        {[['comorbidities','Comorbidities / Chronic Conditions'],['surgicalHistory','Past Surgical History'],['familyHistory','Family History'],['socialHistory','Social History'],['tobaccoUse','Tobacco Use'],['alcoholUse','Alcohol Use'],['drugUse','Substance Use']]
        .map(([k,l])=>{ 
         return (
          <div key={k}>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{l}</label>
            <textarea className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" rows={2} value={(form as any)[k]} onChange={e=>setForm({...form,[k]:e.target.value})}/>
          </div>
          )}
        )}
        <button onClick={saveProfile} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">{saving?'Saving...':'💾 Save Clinical Profile'}</button>
      </div>
    </div>
  );
}

function VitalsTab({patient,vitals,setVitals,saving,setSaving,notify,user}:any){
  const [form,setForm]=useState({
    temperature:'',systolicBp:'',diastolicBp:'',heartRate:'',respiratoryRate:'',
    oxygenSaturation:'',weight:'',height:'',bloodSugar:'',painScore:'',notes:'',
  });

  const isAbnormal=(k:string,v:number)=>{
    const r:Record<string,[number,number]>={
      temperature:[36,37.5],systolicBp:[90,140],diastolicBp:[60,90],
      heartRate:[60,100],respiratoryRate:[12,20],oxygenSaturation:[95,100],bloodSugar:[70,180],
    };
    const range=r[k]; if(!range||!v) return false;
    return v<range[0]||v>range[1];
  };

  const bmi = form.weight&&form.height
    ? (+form.weight/((+form.height/100)**2)).toFixed(1)
    : null;

  const save=async()=>{
    setSaving(true);
    try{
      const clean:any={patientId:patient.id,recordedBy:user?.id,notes:form.notes||null};
      ['temperature','systolicBp','diastolicBp','heartRate','respiratoryRate','oxygenSaturation','weight','height','bloodSugar','painScore']
        .forEach(k=>{const v=(form as any)[k];clean[k]=v===''?null:parseFloat(v)||null;});
      if(bmi) clean.bmi=+bmi;
      await api.recordVitals(clean);
      const rv=await (api.getOPDVitals?.(patient.id)||api.getVitals(patient.id));
      const vlist=rv?.data?.vitals||rv?.data?.data||(Array.isArray(rv?.data)?rv.data:[]);
      setVitals(vlist);
      notify('✅ Vitals recorded!');
      setForm({temperature:'',systolicBp:'',diastolicBp:'',heartRate:'',respiratoryRate:'',oxygenSaturation:'',weight:'',height:'',bloodSugar:'',painScore:'',notes:''});
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  return(
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="card p-5 rounded-2xl lg:col-span-1">
        <h3 className="font-semibold text-slate-700 mb-3">❤️ Record New Vitals</h3>
        <div className="grid grid-cols-2 gap-3 mb-3">
          {[
            ['temperature','Temp (°C)','37.0','0.1'],
            ['systolicBp','Sys BP','120','1'],
            ['diastolicBp','Dia BP','80','1'],
            ['heartRate','Heart Rate','72','1'],
            ['respiratoryRate','Resp Rate','16','1'],
            ['oxygenSaturation','SpO₂ (%)','98','0.1'],
            ['weight','Weight (kg)','70','0.1'],
            ['height','Height (cm)','170','0.1'],
            ['bloodSugar','Blood Sugar','','1'],
            ['painScore','Pain (0-10)','','1'],
          ].map(([k,l,ph,step])=>{
            const v=(form as any)[k];
            const ab=v&&isAbnormal(k,+v);
            return(
              <div key={k}>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{l}</label>
                <input type="number" step={step} placeholder={ph}
                  className={`w-full border rounded-lg p-2 text-center font-bold ${ab?'!border-red-400 bg-red-50 text-red-700':''}`}
                  value={v} onChange={e=>setForm({...form,[k]:e.target.value})}/>
              </div>
            );
          })}
          {bmi&&(
            <div className="col-span-2 bg-blue-50 rounded-lg p-2 text-center">
              <span className="text-xs text-blue-500">Calculated BMI: </span>
              <span className="font-bold text-[#02357A]">{bmi}</span>
            </div>
          )}
        </div>
        <VoiceTextarea label="Notes" rows={2} value={form.notes} onChange={v=>setForm({...form,notes:v})} placeholder="General observations..."/>
        <button onClick={save} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 w-full py-2.5 mt-3">
          {saving?'Saving...':'💾 Save Vitals'}
        </button>
      </div>

      <div className="card p-5 rounded-2xl lg:col-span-2 overflow-x-auto">
        <h3 className="font-semibold text-slate-700 mb-3">📈 Vitals History</h3>
        {vitals.length===0?(
          <p className="text-slate-400 text-sm text-center py-8">No vitals recorded yet</p>
        ):(
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 border-b">
              {['Date','Temp','BP','HR','RR','SpO₂','Weight','BMI','Sugar','Pain'].map(h=><th key={h} className="px-2 py-2 whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {vitals.slice(0,20).map((v:any,i:number)=>(
                <tr key={i} className={i===0?'bg-blue-50':''}>
                  <td className="px-2 py-2 text-xs whitespace-nowrap text-slate-500">{fmtDateTime(v.recordedAt||v.recorded_at)}</td>
                  {[['temperature',v.temperature],['systolicBp',v.systolicBp&&v.diastolicBp?`${v.systolicBp}/${v.diastolicBp}`:null],
                    ['heartRate',v.heartRate],['respiratoryRate',v.respiratoryRate],['oxygenSaturation',v.oxygenSaturation],
                    ['weight',v.weight],['bmi',v.bmi],['bloodSugar',v.bloodSugar],['painScore',v.painScore]].map(([k,val])=>{
                    const ab=val!=null&&k!=='systolicBp'&&k!=='bmi'&&isAbnormal(k as string,+val);
                    return <td key={k as string} className={`px-2 py-2 font-semibold text-center ${ab?'text-red-600':'text-slate-700'}`}>{val??'—'}{ab?' ⚠️':''}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ConsultationTab({patient,user,saving,setSaving,notify,reload}:any){
  const [form,setForm]=useState({
    chiefComplaint:'', historyOfPresentIllness:'', pastMedicalHistory:'',
    physicalExamination:'', systemicExamination:'',
    provisionalDiagnosis:'', finalDiagnosis:'', icdCode:'',
    treatmentPlan:'', advice:'',
    followUpRequired:false, followUpDate:'', followUpReason:'',
  });
  const [history,setHistory]=useState<any[]>([]);

  const loadHistory=async()=>{
    if(!patient?.id)return;
    const r=await api.getConsultations({patientId:patient.id,take:5}).catch(()=>({data:{}}));
    const list=r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
    setHistory(list.filter(Boolean));
  };
  useEffect(()=>{loadHistory();},[patient?.id]);

  const save=async()=>{
    if(!form.chiefComplaint){notify('⚠️ Please enter chief complaint');return;}
    setSaving(true);
    try{
      // Resolve consultationFee — try to get from appointment linked to this patient/doctor
      let consultationFee = 0;
      try {
        const apptRes = await api.getAppointments?.({
          patientId: patient.id,
          doctorId: user?.id,
          status: 'CHECKED_IN',
          take: 1,
        });
        const appt = apptRes?.data?.data?.[0] || apptRes?.data?.[0] || null;
        consultationFee = +(appt?.consultationFee || appt?.consultation_fee || 500);
      } catch { consultationFee = 500; }

      await api.createConsultation({
        ...form,
        patientId:patient.id, doctorId:user?.id,
        consultationDate:new Date().toISOString(),
        followUpDate:form.followUpDate||null,
        followUpReason:form.followUpReason||null,
        consultationStatus:'COMPLETED',
        consultationFee,
      });
      notify('✅ Consultation saved!');
      setForm({chiefComplaint:'',historyOfPresentIllness:'',pastMedicalHistory:'',physicalExamination:'',systemicExamination:'',provisionalDiagnosis:'',finalDiagnosis:'',icdCode:'',treatmentPlan:'',advice:'',followUpRequired:false,followUpDate:'',followUpReason:''});
      loadHistory();reload();
    }catch(e:any){notify('❌ '+(e?.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  return(
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
            <h3 className="font-bold text-slate-800">📝 History & Examination</h3>
          </div>
          <div className="p-5 space-y-4">
            <VoiceTextarea label="Chief Complaint *" rows={3}
              value={form.chiefComplaint} onChange={v=>setForm({...form,chiefComplaint:v})}
              placeholder="e.g. Fever and cough for 3 days, abdominal pain since morning..."/>
            <VoiceTextarea label="History of Present Illness" rows={3}
              value={form.historyOfPresentIllness} onChange={v=>setForm({...form,historyOfPresentIllness:v})}
              placeholder="Onset, duration, progression, associated symptoms..."/>
            <VoiceTextarea label="Past Medical History" rows={3}
              value={form.pastMedicalHistory} onChange={v=>setForm({...form,pastMedicalHistory:v})}
              placeholder="Previous illnesses, surgeries, hospitalizations..."/>
            <VoiceTextarea label="Physical Examination" rows={3}
              value={form.physicalExamination} onChange={v=>setForm({...form,physicalExamination:v})}
              placeholder="General appearance, pallor, icterus, cyanosis, edema..."/>
            <VoiceTextarea label="Systemic Examination" rows={3}
              value={form.systemicExamination} onChange={v=>setForm({...form,systemicExamination:v})}
              placeholder="CVS, RS, P/A, CNS findings..."/>
          </div>
        </div>

        <div className="card rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
            <h3 className="font-bold text-slate-800">📋 Diagnosis & Plan</h3>
          </div>
          <div className="p-5 space-y-4">
            <VoiceTextarea label="Provisional Diagnosis" rows={2}
              value={form.provisionalDiagnosis} onChange={v=>setForm({...form,provisionalDiagnosis:v})}
              placeholder="Working/provisional diagnosis (pending investigations)..."/>
            <VoiceTextarea label="Final Diagnosis *" rows={2}
              value={form.finalDiagnosis} onChange={v=>setForm({...form,finalDiagnosis:v})}
              placeholder="Confirmed diagnosis e.g. Viral fever, Type 2 DM, Hypertension..."/>
            <div>
              <TerminologySearch
                label="ICD-10 Code"
                system="icd10"
                value={form.icdCode}
                onChange={(display:string,code?:string)=>setForm(f=>({...f,icdCode:code||display,finalDiagnosis:f.finalDiagnosis||display}))}
                placeholder="Search ICD-10 e.g. I10 Hypertension, E11 Diabetes..."
              />
              {form.icdCode&&(
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs bg-purple-50 text-purple-700 border border-purple-100 px-2.5 py-1 rounded-lg font-mono font-bold">{form.icdCode}</span>
                  <button onClick={()=>setForm(f=>({...f,icdCode:''}))} className="text-xs text-slate-400 hover:text-red-500">✕ Clear</button>
                </div>
              )}
            </div>
            <VoiceTextarea label="Treatment Plan" rows={3}
              value={form.treatmentPlan} onChange={v=>setForm({...form,treatmentPlan:v})}
              placeholder="Medications, investigations ordered, procedures, monitoring..."/>
            <VoiceTextarea label="Advice & Patient Instructions" rows={3}
              value={form.advice} onChange={v=>setForm({...form,advice:v})}
              placeholder="Diet, activity, warning signs, lifestyle advice, when to return..."/>
            <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
              <label className="flex items-center gap-2.5 cursor-pointer mb-3">
                <input type="checkbox" checked={form.followUpRequired}
                  onChange={e=>setForm({...form,followUpRequired:e.target.checked})} className="w-4 h-4 accent-[#02357A]"/>
                <span className="text-sm font-semibold text-slate-700">Schedule Follow-up</span>
              </label>
              {form.followUpRequired&&(
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Follow-up Date</label>
                    <input type="date" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.followUpDate}
                      onChange={e=>setForm({...form,followUpDate:e.target.value})}
                      min={new Date().toISOString().split('T')[0]}/>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Reason</label>
                    <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.followUpReason}
                      onChange={e=>setForm({...form,followUpReason:e.target.value})}
                      placeholder="Review, results, check progress..."/>
                  </div>
                </div>
              )}
            </div>
            <button onClick={save} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 w-full py-3 font-bold">
              {saving?'Saving…':'💾 Save Consultation Record'}
            </button>
          </div>
        </div>
      </div>

      {/* Consultation History */}
      <div className="card rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
          <h3 className="font-bold text-slate-800">📚 Previous Consultations</h3>
          <span className="text-xs bg-slate-100 text-slate-500 font-semibold px-2.5 py-1 rounded-full">{history.length} records</span>
        </div>
        {history.length===0?(
          <div className="py-10 text-center">
            <p className="text-3xl mb-2">📋</p>
            <p className="text-sm text-slate-400">No consultation records yet</p>
          </div>
        ):(
          <div className="divide-y divide-slate-100">
            {history.map((c:any,i:number)=>(
              <div key={i} className="p-4 hover:bg-slate-50/50 transition-colors">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-xs font-bold text-slate-500">
                    {new Date(c.consultation_date||c.created_at||'').toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                  </span>
                  {c.icd_code&&<span className="text-xs bg-purple-50 text-purple-700 border border-purple-100 px-2 py-0.5 rounded-lg font-mono font-bold">{c.icd_code}</span>}
                  {(()=>{const fn=c.doctor_first_name||'';const ln=c.doctor_last_name||'';const n=`${fn} ${ln}`.trim();return n?<span className="text-xs text-slate-400">{n.startsWith('Dr.')?n:`Dr. ${n}`}{c.doctor_specialization?` · ${c.doctor_specialization}`:''}</span>:null;})()}
                </div>
                {c.chief_complaint&&<p className="text-sm font-semibold text-slate-800">🗣️ {c.chief_complaint}</p>}
                {c.final_diagnosis&&<p className="text-sm text-slate-700 mt-0.5">🔬 <strong>{c.final_diagnosis}</strong></p>}
                {c.treatment_plan&&<p className="text-xs text-slate-500 mt-0.5 line-clamp-1">💊 {c.treatment_plan}</p>}
                {c.follow_up_date&&<p className="text-xs text-blue-600 mt-0.5 font-medium">📅 Follow-up: {new Date(c.follow_up_date).toLocaleDateString('en-IN')}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CPOETab({patient,user,cpoeOrders,setCpoeOrders,saving,setSaving,notify,labTests=[],radServices=[],procedures=[],dietMaster=[],NURSING_TASKS=[]}:any){
  const [showOrder,setShowOrder]=useState(false);
  const [orderType,setOrderType]=useState('PHARMACY');
  const [form,setForm]=useState({
    itemName:'',itemCode:'',dosage:'',route:'Oral',frequency:'Twice daily',duration:'5 days',
    drugId:'',instructions:'',priority:'ROUTINE',contextSetting:'OP',
  });

  const ORDER_META:Record<string,{icon:string;label:string}>={
    PHARMACY:{icon:'💊',label:'Pharmacy / Medication'},
    LAB:{icon:'🔬',label:'Laboratory Investigation'},
    RADIOLOGY:{icon:'🩻',label:'Radiology / Imaging'},
    PROCEDURE:{icon:'🏥',label:'Procedure'},
    DIET:{icon:'🍽️',label:'Diet Order'},
    NURSING:{icon:'👩‍⚕️',label:'Nursing Order'},
  };

  const resetForm=()=>setForm({itemName:'',itemCode:'',dosage:'',route:'Oral',frequency:'Twice daily',duration:'5 days',drugId:'',instructions:'',priority:'ROUTINE',contextSetting:'OP'});

  const save=async()=>{
    if(!form.itemName){notify('⚠️ Enter order item name');return;}
    setSaving(true);
    try{
      await api.createCPOEOrder({...form,patientId:patient.id,doctorId:user?.id,orderType});
      const r=await api.getCPOEOrders({patientId:patient.id});
      setCpoeOrders(Array.isArray(r.data)?r.data:[]);
      notify('✅ Order placed!');
      setShowOrder(false);resetForm();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const updateStatus=async(id:string,status:string)=>{
    try{
      await api.updateCPOEStatus(id,{status,fulfilledBy:user?.id});
      const r=await api.getCPOEOrders({patientId:patient.id});
      setCpoeOrders(Array.isArray(r.data)?r.data:[]);
      notify('✅ Status updated');
    }catch{notify('❌ Error');}
  };

  const grouped:Record<string,any[]> = {};
  cpoeOrders.forEach((o:any)=>{
    const t=o.orderType||o.order_type;
    if(!grouped[t])grouped[t]=[];
    grouped[t].push(o);
  });

  return(
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-slate-700">💊 CPOE Orders</h3>
        <button onClick={()=>setShowOrder(true)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">+ New Order</button>
      </div>

      {cpoeOrders.length===0?(
        <div className="card p-14 text-center rounded-2xl"><span className="text-4xl block mb-2">📋</span><p className="text-slate-400">No orders placed yet</p></div>
      ):(
        <div className="space-y-4">
          {Object.entries(grouped).map(([type,orders])=>{
            const meta=ORDER_META[type]||{icon:'📄',label:type};
            return(
              <div key={type} className="card p-5 rounded-2xl">
                <h4 className="font-bold text-slate-700 text-sm mb-2 flex items-center gap-2">{meta.icon} {meta.label} ({orders.length})</h4>
                <div className="space-y-2">
                  {orders.map((o:any,i:number)=>(
                    <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-xl">
                      <div className="flex-1">
                        <p className="font-semibold text-slate-800 text-sm">{o.itemName||o.item_name}</p>
                        <div className="flex gap-2 mt-0.5 flex-wrap">
                          {o.dosage&&<span className="text-xs text-slate-500">{o.dosage}</span>}
                          {o.route&&<span className="text-xs text-slate-500">· {o.route}</span>}
                          {o.frequency&&<span className="text-xs text-slate-500">· {o.frequency}</span>}
                          {o.duration&&<span className="text-xs text-slate-500">· {o.duration}</span>}
                        </div>
                        {o.instructions&&<p className="text-xs text-amber-600 mt-0.5 italic">💡 {o.instructions}</p>}
                        <p className="text-xs text-slate-400 mt-0.5">{fmtDateTime(o.createdAt||o.created_at)} · {o.orderNumber||o.order_number}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex gap-1.5">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PRIORITY_META[o.priority]||'bg-slate-100'}`}>{o.priority}</span>
                          <span className="text-xs bg-blue-50 text-[#02357A] px-2 py-0.5 rounded-full">{o.contextSetting||o.context_setting}</span>
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                          ${o.status==='COMPLETED'?'bg-green-100 text-green-700':
                            o.status==='CANCELLED'?'bg-red-100 text-red-700':
                            o.status==='IN_PROGRESS'?'bg-amber-100 text-amber-700':
                            'bg-blue-100 text-[#02357A]'}`}>{o.status}</span>
                        {(o.status==='ORDERED'||o.status==='IN_PROGRESS')&&(
                          <div className="flex gap-1">
                            <button onClick={()=>updateStatus(o.id,'COMPLETED')} className="text-xs btn-success py-0.5 px-2">✓</button>
                            <button onClick={()=>updateStatus(o.id,'CANCELLED')} className="text-xs btn-danger py-0.5 px-2">✕</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={showOrder} onClose={()=>setShowOrder(false)} title="💊 New CPOE Order" size="lg">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Order Type *</label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {ORDER_TYPES.map(t=>(
                <button key={t} type="button" onClick={()=>{setOrderType(t);resetForm();}}
                  className={`p-2 rounded-xl border-2 text-xs font-semibold flex flex-col items-center gap-1 transition-all
                    ${orderType===t?'border-[#015CA6] bg-[#02357A] text-white':'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  <span className="text-lg">{ORDER_META[t].icon}</span>
                  <span>{t}</span>
                </button>
              ))}
            </div>
          </div>

          {orderType==='PHARMACY'&&(
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Search Drug from Formulary</label>
              <DrugSearchInput onSelect={(d:any)=>setForm({...form,
                itemName:d.name||d.drugName,itemCode:d.drugCode||d.drug_code||'',
                drugId:d.id,
                route:d.dosageForm==='Injection'?'IV':d.dosageForm==='Inhaler'?'Inhaled':'Oral',
                dosage:d.strength||form.dosage,
              })}/>
              {form.itemName&&<p className="text-xs text-green-600 mt-1">✓ Selected: {form.itemName} {form.itemCode&&`(${form.itemCode})`}</p>}
              {/* Drug interaction + allergy check for CPOE */}
              {form.itemName&&<DrugInteractionAlert
                drugs={cpoeOrders.filter((o:any)=>o.orderType==='PHARMACY'&&o.status!=='CANCELLED').map((o:any)=>o.itemName||'')}
                allergies={[]}
                newDrug={form.itemName}
              />}
            </div>
          )}

          {orderType!=='PHARMACY'&&(
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                {orderType==='LAB'?'Test Name *':orderType==='RADIOLOGY'?'Imaging Study *':orderType==='PROCEDURE'?'Procedure Name *':orderType==='DIET'?'Diet Type *':'Nursing Order *'}
              </label>
              {orderType==='LAB'&&(
                <SearchableSelect
                  options={labTests.map((t:any)=>({value:t.testName||t.test_name,label:t.testName||t.test_name,sublabel:t.testCode||t.test_code,badge:t.testCategory||t.test_category}))}
                  value={form.itemName} onChange={v=>{
                    const matched=labTests.find((t:any)=>(t.testName||t.test_name)===v);
                    setForm({...form,itemName:v,itemCode:matched?.testCode||matched?.test_code||''});
                  }}
                  placeholder="Select lab test..." searchPlaceholder="🔍 Type test name or code (e.g. CBC, LFT, TSH)..." required
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400"
                />
              )}
              {orderType==='RADIOLOGY'&&(
                <SearchableSelect
                  options={radServices.map((s:any)=>({value:s.serviceName||s.service_name,label:s.serviceName||s.service_name,sublabel:s.modality,badge:`₹${s.basePrice||s.base_price||0}`}))}
                  value={form.itemName} onChange={v=>setForm({...form,itemName:v})}
                  placeholder="Select imaging study..." searchPlaceholder="🔍 Type modality or body part (e.g. X-Ray, CT, MRI, USG)..." required
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400"
                />
              )}
              {orderType==='PROCEDURE'&&(
                <SearchableSelect
                  options={procedures.map((p:any)=>({value:p.procedureName||p.procedure_name,label:p.procedureName||p.procedure_name,sublabel:p.category,badge:`₹${p.cost||0}`}))}
                  value={form.itemName} onChange={v=>setForm({...form,itemName:v})}
                  placeholder="Select procedure..." searchPlaceholder="🔍 Type procedure name (e.g. Dressing, ECG, IV Fluid)..." required
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400"
                />
              )}
              {orderType==='DIET'&&(
                <SearchableSelect
                  options={dietMaster.map((d:any)=>({value:d.dietName||d.diet_name||d.name,label:d.dietName||d.diet_name||d.name,sublabel:`${d.caloriesKcal||d.calories_kcal||0} kcal`,badge:d.dietCode||d.diet_code||d.code}))}
                  value={form.itemName} onChange={v=>setForm({...form,itemName:v})}
                  placeholder="Select diet type..." searchPlaceholder="🔍 Type diet name (e.g. Diabetic, NPO, Soft Diet)..." required
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400"
                />
              )}
              {orderType!=='LAB'&&orderType!=='RADIOLOGY'&&orderType!=='PROCEDURE'&&orderType!=='DIET'&&(
                <SearchableSelect
                  options={NURSING_TASKS}
                  value={form.itemName} onChange={v=>setForm({...form,itemName:v})}
                  placeholder="Select nursing order..." searchPlaceholder="🔍 Type nursing order (e.g. Bed Rest, Vitals, Dressing)..." required
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400"
                />
              )}
            </div>
          )}

          {orderType==='PHARMACY'&&(
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Dosage</label>
                <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.dosage} onChange={e=>setForm({...form,dosage:e.target.value})} placeholder="500mg"/>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Route</label>
                <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.route} onChange={(e:any)=>setForm({...form,route:e.target.value})}>
                  {['Oral','IV','IM','SC','Topical','Inhaled','Sublingual'].map(r=><option key={r}>{r}</option>)}
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Frequency</label>
                <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.frequency} onChange={(e:any)=>setForm({...form,frequency:e.target.value})}>
                  {['Once daily','Twice daily','Thrice daily','Every 6 hours','Every 8 hours','SOS'].map(f=><option key={f}>{f}</option>)}
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Duration</label>
                <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.duration} onChange={e=>setForm({...form,duration:e.target.value})} placeholder="5 days"/>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Priority</label>
              <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.priority} onChange={(e:any)=>setForm({...form,priority:e.target.value})}>
                {PRIORITIES.map(p=><option key={p}>{p}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Setting</label>
              <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.contextSetting} onChange={(e:any)=>setForm({...form,contextSetting:e.target.value})}>
                <option value="OP">Outpatient (OP)</option>
                <option value="IP">Inpatient (IP)</option>
              </Select>
            </div>
          </div>

          <VoiceTextarea label="Instructions / Notes" rows={2}
            value={form.instructions} onChange={v=>setForm({...form,instructions:v})}
            placeholder="Special instructions for this order..."/>

          <div className="flex gap-3">
            <button onClick={save} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 flex-1 py-2.5">{saving?'Placing...':'📋 Place Order'}</button>
            <button onClick={()=>setShowOrder(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 px-6">Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ReferralTab({patient,user,referrals,setReferrals,doctors,saving,setSaving,notify}:any){
  const [showForm,setShowForm]=useState(false);
  const [form,setForm]=useState({
    referredToDepartment:'',referredToDoctorId:'',referredToFacility:'',
    reason:'',clinicalSummary:'',urgency:'ROUTINE',notes:'',
  });

  const DEPARTMENTS=['Cardiology','Neurology','Orthopedics','Gastroenterology','Nephrology','Pulmonology',
    'Endocrinology','Dermatology','Psychiatry','ENT','Ophthalmology','Gynecology','Urology','Oncology','General Surgery'];

  const save=async()=>{
    if(!form.referredToDepartment||!form.reason){notify('⚠️ Department and reason are required');return;}
    setSaving(true);
    try{
      await api.createReferral({...form,patientId:patient.id,referringDoctorId:user?.id});
      const r=await api.getReferrals({patientId:patient.id});
      setReferrals(Array.isArray(r.data)?r.data:[]);
      notify('✅ Referral created!');
      setShowForm(false);
      setForm({referredToDepartment:'',referredToDoctorId:'',referredToFacility:'',reason:'',clinicalSummary:'',urgency:'ROUTINE',notes:''});
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const updateStatus=async(id:string,status:string)=>{
    try{
      await api.updateReferralStatus(id,status);
      const r=await api.getReferrals({patientId:patient.id});
      setReferrals(Array.isArray(r.data)?r.data:[]);
      notify('✅ Updated');
    }catch{notify('❌ Error');}
  };

  return(
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-slate-700">↗️ Referrals</h3>
        <button onClick={()=>setShowForm(true)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">+ New Referral</button>
      </div>
      {referrals.length===0?(
        <div className="card p-14 text-center rounded-2xl"><span className="text-4xl block mb-2">↗️</span><p className="text-slate-400">No referrals</p></div>
      ):(
        <div className="space-y-3">
          {referrals.map((r:any,i:number)=>(
            <div key={i} className="card p-5 rounded-2xl">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-slate-800">{r.referredToDepartment||r.referred_to_department}</p>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">{r.referralNumber||r.referral_number}</p>
                  <p className="text-sm text-slate-600 mt-1">{r.reason}</p>
                  {r.referredToFacility&&<p className="text-xs text-slate-500 mt-1">🏥 {r.referredToFacility}</p>}
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PRIORITY_META[r.urgency]||'bg-slate-100'}`}>{r.urgency}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                    ${r.status==='ACCEPTED'?'bg-green-100 text-green-700':
                      r.status==='COMPLETED'?'bg-blue-100 text-[#02357A]':
                      r.status==='CANCELLED'?'bg-red-100 text-red-700':
                      'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                  {r.status==='PENDING'&&(
                    <button onClick={()=>updateStatus(r.id,'CANCELLED')} className="text-xs btn-danger py-0.5 px-2">Cancel</button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">{fmtDateTime(r.createdAt||r.created_at)}</p>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={showForm} onClose={()=>setShowForm(false)} title="↗️ New Referral" size="lg">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Referred to Department *</label>
              <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.referredToDepartment} onChange={(e:any)=>setForm({...form,referredToDepartment:e.target.value})}>
                <option value="">— Select Department —</option>
                {DEPARTMENTS.map(d=><option key={d}>{d}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Referred to Doctor</label>
              <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.referredToDoctorId} onChange={(e:any)=>setForm({...form,referredToDoctorId:e.target.value})}>
                <option value="">— Any available —</option>
                {(doctors||[]).map((d:any)=>(
                  <option key={d.id} value={d.id}>Dr. {d.firstName||d.first_name} {d.lastName||d.last_name}</option>
                ))}
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">External Facility (if referring outside)</label>
            <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.referredToFacility} onChange={e=>setForm({...form,referredToFacility:e.target.value})}
              placeholder="e.g. City Heart Institute (leave blank for internal referral)"/>
          </div>
          <VoiceTextarea label="Reason for Referral *" rows={2}
            value={form.reason} onChange={v=>setForm({...form,reason:v})}
            placeholder="Why is this patient being referred?"/>
          <VoiceTextarea label="Clinical Summary" rows={3}
            value={form.clinicalSummary} onChange={v=>setForm({...form,clinicalSummary:v})}
            placeholder="Relevant history, findings, investigations done so far..."/>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Urgency</label>
            <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.urgency} onChange={(e:any)=>setForm({...form,urgency:e.target.value})}>
              {['ROUTINE','URGENT','EMERGENCY'].map(u=><option key={u}>{u}</option>)}
            </Select>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 flex-1 py-2.5">{saving?'Creating...':'↗️ Create Referral'}</button>
            <button onClick={()=>setShowForm(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 px-6">Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function DocumentsTab({patient,user,documents,setDocuments,saving,setSaving,notify}:any){
  const [showForm,setShowForm]=useState(false);
  const [form,setForm]=useState({documentName:'',documentType:'LAB_REPORT',fileUrl:''});

  const DOC_TYPES=[
    {code:'LAB_REPORT',icon:'🔬',label:'Lab Report'},
    {code:'RADIOLOGY',icon:'🩻',label:'Radiology Report'},
    {code:'PRESCRIPTION',icon:'💊',label:'Prescription'},
    {code:'DISCHARGE_SUMMARY',icon:'📄',label:'Discharge Summary'},
    {code:'CONSENT',icon:'✍️',label:'Consent Form'},
    {code:'INSURANCE',icon:'🪪',label:'Insurance Document'},
    {code:'OTHER',icon:'📁',label:'Other'},
  ];

  const save=async()=>{
    if(!form.documentName){notify('⚠️ Enter document name');return;}
    setSaving(true);
    try{
      await api.addClinicalDocument({...form,patientId:patient.id,uploadedBy:user?.id});
      const r=await api.getClinicalDocuments(patient.id);
      setDocuments(Array.isArray(r.data)?r.data:[]);
      notify('✅ Document added!');
      setShowForm(false);
      setForm({documentName:'',documentType:'LAB_REPORT',fileUrl:''});
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  return(
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-slate-700">📁 Clinical Documents</h3>
        <button onClick={()=>setShowForm(true)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">+ Add Document Reference</button>
      </div>
      {documents.length===0?(
        <div className="card p-14 text-center rounded-2xl"><span className="text-4xl block mb-2">📁</span><p className="text-slate-400">No documents uploaded</p></div>
      ):(
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {documents.map((d:any,i:number)=>{
            const meta=DOC_TYPES.find(t=>t.code===(d.documentType||d.document_type))||DOC_TYPES[6];
            return(
              <div key={i} className="card p-4 text-center hover:shadow-md transition-all">
                <span className="text-3xl block mb-2">{meta.icon}</span>
                <p className="font-semibold text-slate-800 text-sm truncate">{d.documentName||d.document_name}</p>
                <p className="text-xs text-slate-400 mt-1">{meta.label}</p>
                <p className="text-xs text-slate-400 mt-1">{fmtDate(d.uploadedAt||d.uploaded_at)}</p>
                {(d.fileUrl||d.file_url)&&(
                  <a href={d.fileUrl||d.file_url} target="_blank" rel="noreferrer" className="text-xs text-[#02357A] hover:underline mt-2 inline-block">🔗 View</a>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={showForm} onClose={()=>setShowForm(false)} title="📁 Add Document Reference" size="sm">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Document Name *</label>
            <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.documentName} onChange={e=>setForm({...form,documentName:e.target.value})}
              placeholder="e.g. CBC Report 23-May-2026"/>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Document Type</label>
            <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.documentType} onChange={(e:any)=>setForm({...form,documentType:e.target.value})}>
              {DOC_TYPES.map(t=><option key={t.code} value={t.code}>{t.icon} {t.label}</option>)}
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">File URL / Reference Link</label>
            <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.fileUrl} onChange={e=>setForm({...form,fileUrl:e.target.value})}
              placeholder="https://... (optional)"/>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 flex-1">{saving?'Saving...':'Add Document'}</button>
            <button onClick={()=>setShowForm(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 px-6">Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

async function printPrescription(rx:any,patient:any,doctor:any){
  const {openPrintWindow:printWin} = await import('../utils/printUtils');
  const items=(rx.items||[]).map((it:any,i:number)=>`
    <tr><td>${i+1}</td><td><strong>${it.drugName||it.name||'—'}</strong>${it.strength?` (${it.strength})`:''}</td>
    <td>${it.dosage||'—'}</td><td>${it.frequency||'—'}</td><td>${it.duration||'—'}</td>
    <td>${it.instructions||'—'}</td></tr>`).join('');
  const patName=`${patient.firstName||patient.first_name||''} ${patient.lastName||patient.last_name||''}`.trim();
  const html=`
    <div class="section">
      <div class="row">
        <div class="field"><div class="lbl">Patient</div><div class="val">${patName}</div></div>
        <div class="field"><div class="lbl">UHID</div><div class="val">${patient.uhid||'—'}</div></div>
        <div class="field"><div class="lbl">Age / Sex</div><div class="val">${calcAge(patient.dateOfBirth||patient.date_of_birth)||'—'} / ${patient.gender||'—'}</div></div>
        <div class="field"><div class="lbl">Date</div><div class="val">${new Date().toLocaleDateString('en-IN')}</div></div>
      </div>
      ${(patient.knownAllergies||patient.known_allergies)?`<div style="background:#fee2e2;border:1px solid #fca5a5;border-radius:4px;padding:4px 8px;margin:6px 0;font-size:10px;color:#dc2626;font-weight:700;">⚠️ Allergies: ${patient.knownAllergies||patient.known_allergies}</div>`:''}
    </div>
    <div style="font-size:28px;font-weight:900;color:#1e3a5f;margin:8px 0 4px;">℞</div>
    <table>
      <thead><tr><th>#</th><th>Medicine</th><th>Dosage</th><th>Frequency</th><th>Duration</th><th>Instructions</th></tr></thead>
      <tbody>${items||'<tr><td colspan="6" style="text-align:center;color:#888;">No medications</td></tr>'}</tbody>
    </table>
    ${rx.specialInstructions?`<div class="section" style="margin-top:10px;"><h4>Special Instructions</h4><p>${rx.specialInstructions}</p></div>`:''}
    ${rx.dietaryAdvice?`<div class="section"><h4>Dietary Advice</h4><p>${rx.dietaryAdvice}</p></div>`:''}
    <div style="margin-top:30px;text-align:right;">
      <div style="display:inline-block;text-align:center;">
        <div style="border-top:1px solid #333;width:180px;margin-bottom:3px;margin-left:auto;"></div>
        <div style="font-size:11px;font-weight:700;">Dr. ${doctor?.firstName||''} ${doctor?.lastName||''}</div>
        <div style="font-size:9px;color:#888;">${doctor?.specialization||''}</div>
      </div>
    </div>`;
  await printWin(html,'Prescription','Valid for 7 days from date of issue. This is a computer generated prescription.');
}

function PrescriptionTab({patient,user,prescriptions,setPrescriptions,saving,setSaving,notify,profile}:any){
  const [showForm,setShowForm]=useState(false);
  const [items,setItems]=useState<any[]>([]);
  const [form,setForm]=useState({specialInstructions:'',dietaryAdvice:'',restrictions:''});
  const [lastAddedDrug,setLastAddedDrug]=useState('');
  const allergies:string[]=(profile?.allergies||[]).map((a:any)=>a.allergen||a);

  const addDrug=(d:any)=>{
    const drugName=d.name||d.drugName||'';
    setLastAddedDrug(drugName);
    setItems([...items,{
      drugId:d.id, drugName, drugCode:d.drugCode||d.drug_code,
      strength:d.strength, dosage:d.strength||'', route: d.dosageForm==='Injection'?'IV':'Oral',
      frequency:'Twice daily', duration:'5 days', instructions:'',
    }]);
  };
  const updateItem=(i:number,key:string,val:string)=>{
    const copy=[...items]; copy[i]={...copy[i],[key]:val}; setItems(copy);
  };
  const removeItem=(i:number)=>setItems(items.filter((_,idx)=>idx!==i));

  const save=async()=>{
    if(items.length===0){notify('⚠️ Add at least one medicine');return;}
    setSaving(true);
    try{
      const r=await api.createPrescription({
        patientId:patient.id, doctorId:user?.id,
        prescriptionDate:new Date().toISOString().split('T')[0],
        items, ...form, status:'ACTIVE',
      });
      const list=await api.getPrescriptions(patient.id);
      setPrescriptions(Array.isArray(list.data)?list.data:[]);
      notify('✅ Prescription created!');
      printPrescription(r.data,patient,user);
      setShowForm(false);setItems([]);
      setForm({specialInstructions:'',dietaryAdvice:'',restrictions:''});
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  return(
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-slate-700">🖨️ Prescriptions</h3>
        <button onClick={()=>setShowForm(true)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">+ New Prescription</button>
      </div>

      {prescriptions.length===0?(
        <div className="card p-14 text-center rounded-2xl"><span className="text-4xl block mb-2">🖨️</span><p className="text-slate-400">No prescriptions yet</p></div>
      ):(
        <div className="space-y-3">
          {prescriptions.map((rx:any,i:number)=>(
            <div key={i} className="card p-5 rounded-2xl">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-bold text-slate-800">{rx.prescriptionNumber||rx.prescription_number||'Prescription'}</p>
                  <p className="text-xs text-slate-400">{fmtDate(rx.prescriptionDate||rx.prescription_date||rx.createdAt)}</p>
                </div>
                <button onClick={()=>printPrescription(rx,patient,user)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs py-1.5 px-3">🖨️ Print</button>
              </div>
              <div className="space-y-1">
                {(rx.items||[]).map((it:any,j:number)=>(
                  <div key={j} className="flex justify-between text-sm bg-slate-50 rounded-lg px-3 py-1.5">
                    <span className="font-medium">{it.drugName||it.name} {it.strength&&`(${it.strength})`}</span>
                    <span className="text-slate-500 text-xs">{it.dosage} · {it.frequency} · {it.duration}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={showForm} onClose={()=>setShowForm(false)} title="🖨️ New Prescription" size="xl">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Search & Add Medicine</label>
            <DrugSearchInput onSelect={addDrug}/>
            {/* Drug interaction + allergy alerts */}
            {items.length>0&&<DrugInteractionAlert drugs={items.map((it:any)=>it.drugName)} allergies={allergies} newDrug={lastAddedDrug}/>}
          </div>

          {items.length>0&&(
            <div className="space-y-2">
              {items.map((it,i)=>(
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-xl p-2">
                  <div className="col-span-3">
                    <p className="font-semibold text-sm text-slate-800">{it.drugName} {it.strength&&`(${it.strength})`}</p>
                  </div>
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" placeholder="Dosage" value={it.dosage}
                    onChange={e=>updateItem(i,'dosage',e.target.value)}/>
                  <select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={it.route} onChange={e=>updateItem(i,'route',e.target.value)}>
                    {['Oral','IV','IM','SC','Topical','Inhaled'].map(r=><option key={r}>{r}</option>)}
                  </select>
                  <select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={it.frequency} onChange={e=>updateItem(i,'frequency',e.target.value)}>
                    {['Once daily','Twice daily','Thrice daily','Every 6 hours','SOS'].map(f=><option key={f}>{f}</option>)}
                  </select>
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" placeholder="Duration" value={it.duration}
                    onChange={e=>updateItem(i,'duration',e.target.value)}/>
                  <button onClick={()=>removeItem(i)} className="text-red-400 hover:text-red-600 col-span-1 text-center">✕</button>
                  <input className="w-full border rounded-lg p-2 col-span-12" placeholder="Instructions (e.g. after food)" value={it.instructions}
                    onChange={e=>updateItem(i,'instructions',e.target.value)}/>
                </div>
              ))}
            </div>
          )}

          <VoiceTextarea label="Special Instructions" rows={2} value={form.specialInstructions}
            onChange={v=>setForm({...form,specialInstructions:v})} placeholder="General instructions for the patient..."/>
          <VoiceTextarea label="Dietary Advice" rows={2} value={form.dietaryAdvice}
            onChange={v=>setForm({...form,dietaryAdvice:v})} placeholder="Dietary recommendations..."/>

          <div className="flex gap-3">
            <button onClick={save} disabled={saving||items.length===0} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 flex-1 py-2.5 font-semibold">
              {saving?'Generating...':'🖨️ Generate & Print Prescription'}
            </button>
            <button onClick={()=>setShowForm(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 px-6">Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function printDischargeSummary(ds:any,patient:any,doctor:any){
  const meds=(arr:any[])=>arr&&arr.length?arr.map((m:any)=>`<li>${m.name||m.drugName} ${m.dosage||''} — ${m.frequency||''} ${m.duration?'for '+m.duration:''} ${m.instructions?'('+m.instructions+')':''}</li>`).join(''):'<li>None</li>';
  const html=`<!DOCTYPE html><html><head><title>Discharge Summary</title>
  <style>body{font-family:Arial,sans-serif;padding:24px;font-size:13px;line-height:1.5;}
  .header{text-align:center;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:14px;}
  .row{display:flex;justify-content:space-between;margin:3px 0;font-size:12px;}
  .section{margin-top:14px;}.section h4{background:#333;color:#fff;padding:4px 8px;margin:0 0 6px;font-size:12px;}
  .section p{margin:2px 0 8px;white-space:pre-wrap;}
  ul{margin:4px 0 8px 18px;padding:0;}
  .footer{margin-top:40px;display:flex;justify-content:flex-end;}
  .sign{text-align:center;}.sign-line{border-top:1px solid #000;width:200px;margin-bottom:4px;}
  @media print{body{padding:10px;}}</style></head><body>
  <div class="header"><h2>🏥 City Hospital</h2><p><strong>DISCHARGE SUMMARY</strong></p></div>
  <div class="row"><span><strong>Summary No:</strong> ${ds.summaryNumber||ds.summary_number||'—'}</span>
    <span><strong>Status:</strong> ${ds.status}</span></div>
  <div class="row"><span><strong>Patient:</strong> ${(patient.firstName||patient.first_name)+' '+(patient.lastName||patient.last_name)}</span>
    <span><strong>UHID:</strong> ${patient.uhid}</span></div>
  <div class="row"><span><strong>Age/Sex:</strong> ${calcAge(patient.dateOfBirth||patient.date_of_birth)} / ${patient.gender}</span>
    <span><strong>Blood Group:</strong> ${patient.bloodGroup||patient.blood_group||'—'}</span></div>
  <div class="row"><span><strong>Admission Date:</strong> ${ds.admissionDate?new Date(ds.admissionDate).toLocaleDateString('en-IN'):'—'}</span>
    <span><strong>Discharge Date:</strong> ${ds.dischargeDate?new Date(ds.dischargeDate).toLocaleDateString('en-IN'):'—'}</span></div>

  <div class="section"><h4>CHIEF COMPLAINTS</h4><p>${ds.chiefComplaints||'—'}</p></div>
  <div class="section"><h4>CLINICAL FINDINGS</h4><p>${ds.clinicalFindings||'—'}</p></div>
  <div class="section"><h4>PROVISIONAL DIAGNOSIS</h4><p>${ds.provisionalDiagnosis||'—'}</p></div>
  <div class="section"><h4>FINAL DIAGNOSIS</h4><p>${ds.finalDiagnosis||'—'}</p></div>
  <div class="section"><h4>PROCEDURES PERFORMED</h4><p>${ds.proceduresPerformed||'None'}</p></div>
  <div class="section"><h4>HOSPITAL COURSE</h4><p>${ds.hospitalCourse||'—'}</p></div>
  <div class="section"><h4>INVESTIGATION RESULTS</h4><p>${ds.investigationResults||'—'}</p></div>
  <div class="section"><h4>MEDICATIONS ADMINISTERED DURING STAY</h4><ul>${meds(ds.medicationsAdministered)}</ul></div>
  <div class="section"><h4>DISCHARGE MEDICATIONS</h4><ul>${meds(ds.dischargeMedications)}</ul></div>
  <div class="section"><h4>CONDITION AT DISCHARGE</h4><p>${ds.conditionAtDischarge||'—'} · Disposition: ${ds.dischargeDisposition||'—'}</p></div>
  <div class="section"><h4>DIET ADVICE</h4><p>${ds.dietAdvice||'—'}</p></div>
  <div class="section"><h4>ACTIVITY ADVICE</h4><p>${ds.activityAdvice||'—'}</p></div>
  <div class="section"><h4>WARNING SIGNS — RETURN IMMEDIATELY IF</h4><p>${ds.warningSigns||'—'}</p></div>
  <div class="section"><h4>FOLLOW-UP</h4><p>${ds.followUpInstructions||'—'} ${ds.followUpDate?'· Date: '+new Date(ds.followUpDate).toLocaleDateString('en-IN'):''}</p></div>

  <div class="footer"><div class="sign"><div class="sign-line"></div><p>Dr. ${doctor?.firstName||''} ${doctor?.lastName||''}</p></div></div>
  </body></html>`;
  const w=window.open('','_blank','width=850,height=900');
  if(!w)return;
  w.document.write(html);w.document.close();w.onload=()=>w.print();
}

const DS_EMPTY = {
  admissionId:'', admissionDate:'', dischargeDate:'',
  chiefComplaints:'', clinicalFindings:'', provisionalDiagnosis:'', finalDiagnosis:'',
  proceduresPerformed:'', hospitalCourse:'', investigationResults:'',
  conditionAtDischarge:'STABLE', dischargeDisposition:'HOME',
  followUpInstructions:'', followUpDate:'', dietAdvice:'', activityAdvice:'', warningSigns:'',
  medicationsAdministered:[] as any[], dischargeMedications:[] as any[],
};

function DischargeSummaryTab({patient,user,saving,setSaving,notify}:any){
  const [summaries,setSummaries]=useState<any[]>([]);
  const [admissions,setAdmissions]=useState<any[]>([]);
  const [editing,setEditing]=useState<any>(null);
  const [form,setForm]=useState<any>(DS_EMPTY);
  const [loading,setLoading]=useState(false);
  const [newMed,setNewMed]=useState('');

  const load=async()=>{
    setLoading(true);
    try{
      const [s,a]=await Promise.all([
        api.getDischargeByPatient(patient.id),
        api.getPatientAdmissions(patient.id).catch(()=>({data:[]})),
      ]);
      setSummaries(Array.isArray(s.data)?s.data:[]);
      setAdmissions(Array.isArray(a.data)?a.data:safeArray(a.data));
    }catch{}finally{setLoading(false);}
  };
  useEffect(()=>{load();},[patient.id]);

  const startNew=async(admissionId?:string)=>{
    setForm(DS_EMPTY);
    setEditing('new');
    if(admissionId){
      try{
        const r=await api.generateDischargeDraft(admissionId);
        const d=r.data||{};
        setForm({
          ...DS_EMPTY,...d,
          admissionDate: d.admission_date||d.admissionDate?new Date(d.admission_date||d.admissionDate).toISOString().slice(0,16):'',
          dischargeDate: d.discharge_date||d.dischargeDate?new Date(d.discharge_date||d.dischargeDate).toISOString().slice(0,16):'',
          medicationsAdministered: d.medicationsAdministered||[],
        });
        notify('✅ Draft auto-populated from admission data');
      }catch{notify('⚠️ Could not auto-populate — fill manually');}
    }
  };

  const editExisting=(ds:any)=>{
    setForm({...DS_EMPTY,...ds,
      admissionDate: ds.admissionDate?new Date(ds.admissionDate).toISOString().slice(0,16):'',
      dischargeDate: ds.dischargeDate?new Date(ds.dischargeDate).toISOString().slice(0,16):'',
      followUpDate: ds.followUpDate?new Date(ds.followUpDate).toISOString().slice(0,10):'',
      medicationsAdministered: ds.medicationsAdministered||[],
      dischargeMedications: ds.dischargeMedications||[],
    });
    setEditing(ds.id);
  };

  const save=async(finalize=false)=>{
    setSaving(true);
    try{
      let result;
      if(editing==='new'){
        result=await api.createDischargeSummary({...form,patientId:patient.id,doctorId:user?.id,
          status: finalize?'FINALIZED':'DRAFT'});
      }else{
        result=await api.updateDischargeSummary(editing,{...form,status: finalize?'FINALIZED':form.status});
        if(finalize) await api.finalizeDischargeSummary(editing);
      }
      notify(finalize?'✅ Discharge summary finalized!':'✅ Draft saved!');
      setEditing(null);
      load();
      if(finalize) printDischargeSummary(result.data,patient,user);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const addMed=(key:'medicationsAdministered'|'dischargeMedications')=>{
    if(!newMed.trim())return;
    setForm({...form,[key]:[...form[key],{name:newMed,dosage:'',frequency:'',duration:'',instructions:''}]});
    setNewMed('');
  };
  const removeMed=(key:'medicationsAdministered'|'dischargeMedications',i:number)=>{
    setForm({...form,[key]:form[key].filter((_:any,idx:number)=>idx!==i)});
  };
  const updateMed=(key:'medicationsAdministered'|'dischargeMedications',i:number,field:string,val:string)=>{
    const copy=[...form[key]]; copy[i]={...copy[i],[field]:val}; setForm({...form,[key]:copy});
  };

  if(!editing) return(
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-slate-700">📄 Discharge Summaries</h3>
        <div className="flex gap-2">
          {admissions.filter((a:any)=>a.status==='DISCHARGED'||a.status==='ADMITTED').slice(0,1).map((a:any)=>(
            <button key={a.id} onClick={()=>startNew(a.id)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">
              📄 Generate from {a.ipdNumber||a.ipd_number||'Admission'}
            </button>
          ))}
          <button onClick={()=>startNew()} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-sm">+ Blank Summary</button>
        </div>
      </div>

      {loading?(
        <div className="card p-10 text-center rounded-2xl"><div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
      ):summaries.length===0?(
        <div className="card p-14 text-center rounded-2xl"><span className="text-4xl block mb-2">📄</span><p className="text-slate-400">No discharge summaries yet</p></div>
      ):(
        <div className="space-y-3">
          {summaries.map((ds:any,i:number)=>(
            <div key={i} className="card p-5 rounded-2xl">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-slate-800">{ds.summaryNumber||ds.summary_number}</p>
                  <p className="text-sm text-slate-600 mt-1">{ds.finalDiagnosis||ds.final_diagnosis||'No diagnosis recorded'}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Admitted: {fmtDate(ds.admissionDate||ds.admission_date)} · Discharged: {fmtDate(ds.dischargeDate||ds.discharge_date)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ds.status==='FINALIZED'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>
                    {ds.status==='FINALIZED'?'✅ Finalized':'📝 Draft'}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={()=>editExisting(ds)} className="text-xs btn-secondary py-1 px-2">{ds.status==='FINALIZED'?'👁️ View':'✏️ Edit'}</button>
                    <button onClick={()=>printDischargeSummary(ds,patient,user)} className="text-xs btn-secondary py-1 px-2">🖨️ Print</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const readOnly = form.status==='FINALIZED';
  return(
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-700">
          {editing==='new'?'📄 New Discharge Summary':`📄 Discharge Summary — ${form.summaryNumber||''}`}
          {readOnly&&<span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">FINALIZED — Read Only</span>}
        </h3>
        <button onClick={()=>setEditing(null)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs py-1.5 px-3">← Back to List</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5 space-y-4 rounded-2xl">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Admission Date/Time</label>
              <input type="datetime-local" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" disabled={readOnly} value={form.admissionDate}
                onChange={e=>setForm({...form,admissionDate:e.target.value})}/>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Discharge Date/Time</label>
              <input type="datetime-local" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" disabled={readOnly} value={form.dischargeDate}
                onChange={e=>setForm({...form,dischargeDate:e.target.value})}/>
            </div>
          </div>
          {readOnly?(
            <>
              <ReadField label="Chief Complaints" value={form.chiefComplaints}/>
              <ReadField label="Clinical Findings" value={form.clinicalFindings}/>
              <ReadField label="Provisional Diagnosis" value={form.provisionalDiagnosis}/>
              <ReadField label="Final Diagnosis" value={form.finalDiagnosis}/>
              <ReadField label="Procedures Performed" value={form.proceduresPerformed}/>
              <ReadField label="Hospital Course" value={form.hospitalCourse}/>
              <ReadField label="Investigation Results" value={form.investigationResults}/>
            </>
          ):(
            <>
              <VoiceTextarea label="Chief Complaints" rows={2} value={form.chiefComplaints} onChange={v=>setForm({...form,chiefComplaints:v})}/>
              <VoiceTextarea label="Clinical Findings" rows={2} value={form.clinicalFindings} onChange={v=>setForm({...form,clinicalFindings:v})}/>
              <VoiceTextarea label="Provisional Diagnosis" rows={2} value={form.provisionalDiagnosis} onChange={v=>setForm({...form,provisionalDiagnosis:v})}/>
              <VoiceTextarea label="Final Diagnosis *" rows={2} value={form.finalDiagnosis} onChange={v=>setForm({...form,finalDiagnosis:v})}/>
              <VoiceTextarea label="Procedures Performed" rows={2} value={form.proceduresPerformed} onChange={v=>setForm({...form,proceduresPerformed:v})}/>
              <VoiceTextarea label="Hospital Course Summary" rows={4} value={form.hospitalCourse} onChange={v=>setForm({...form,hospitalCourse:v})}
                placeholder="Day-wise course, response to treatment, complications if any..."/>
              <VoiceTextarea label="Investigation Results" rows={3} value={form.investigationResults} onChange={v=>setForm({...form,investigationResults:v})}
                placeholder="Key lab/imaging findings during stay..."/>
            </>
          )}
        </div>

        <div className="card p-5 space-y-4 rounded-2xl">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Medications Administered During Stay</label>
            <div className="space-y-1.5">
              {form.medicationsAdministered.map((m:any,i:number)=>(
                <div key={i} className="flex items-center gap-1.5 bg-slate-50 rounded-lg p-1.5">
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all" disabled={readOnly} placeholder="Drug" value={m.name||''} onChange={e=>updateMed('medicationsAdministered',i,'name',e.target.value)}/>
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all" disabled={readOnly} placeholder="Dosage" value={m.dosage||''} onChange={e=>updateMed('medicationsAdministered',i,'dosage',e.target.value)}/>
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all" disabled={readOnly} placeholder="Frequency" value={m.frequency||''} onChange={e=>updateMed('medicationsAdministered',i,'frequency',e.target.value)}/>
                  {!readOnly&&<button onClick={()=>removeMed('medicationsAdministered',i)} className="text-red-400 px-1">✕</button>}
                </div>
              ))}
              {!readOnly&&(
                <div className="flex gap-1.5">
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" placeholder="Add medication..." value={newMed} onChange={e=>setNewMed(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),addMed('medicationsAdministered'))}/>
                  <button onClick={()=>addMed('medicationsAdministered')} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs px-3">+ Add</button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Discharge Medications (to continue at home)</label>
            <div className="space-y-1.5">
              {form.dischargeMedications.map((m:any,i:number)=>(
                <div key={i} className="flex items-center gap-1.5 bg-green-50 rounded-lg p-1.5">
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all" disabled={readOnly} placeholder="Drug" value={m.name||''} onChange={e=>updateMed('dischargeMedications',i,'name',e.target.value)}/>
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all" disabled={readOnly} placeholder="Dosage" value={m.dosage||''} onChange={e=>updateMed('dischargeMedications',i,'dosage',e.target.value)}/>
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all" disabled={readOnly} placeholder="Frequency" value={m.frequency||''} onChange={e=>updateMed('dischargeMedications',i,'frequency',e.target.value)}/>
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all" disabled={readOnly} placeholder="Duration" value={m.duration||''} onChange={e=>updateMed('dischargeMedications',i,'duration',e.target.value)}/>
                  {!readOnly&&<button onClick={()=>removeMed('dischargeMedications',i)} className="text-red-400 px-1">✕</button>}
                </div>
              ))}
              {!readOnly&&(
                <div className="flex gap-1.5">
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" placeholder="Add discharge medication..." value={newMed} onChange={e=>setNewMed(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),addMed('dischargeMedications'))}/>
                  <button onClick={()=>addMed('dischargeMedications')} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs px-3">+ Add</button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Condition at Discharge</label>
              <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.conditionAtDischarge} onChange={(e:any)=>setForm({...form,conditionAtDischarge:e.target.value})} disabled={readOnly}>
                {['STABLE','IMPROVED','CURED','UNCHANGED','REFERRED','DAMA','EXPIRED'].map(c=><option key={c}>{c}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Disposition</label>
              <Select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.dischargeDisposition} onChange={(e:any)=>setForm({...form,dischargeDisposition:e.target.value})} disabled={readOnly}>
                {['HOME','REFERRED','DAMA','EXPIRED','TRANSFERRED'].map(d=><option key={d}>{d}</option>)}
              </Select>
            </div>
          </div>

          {readOnly?(
            <>
              <ReadField label="Diet Advice" value={form.dietAdvice}/>
              <ReadField label="Activity Advice" value={form.activityAdvice}/>
              <ReadField label="Warning Signs" value={form.warningSigns}/>
              <ReadField label="Follow-up Instructions" value={form.followUpInstructions}/>
            </>
          ):(
            <>
              <VoiceTextarea label="Diet Advice" rows={2} value={form.dietAdvice} onChange={v=>setForm({...form,dietAdvice:v})}/>
              <VoiceTextarea label="Activity Advice" rows={2} value={form.activityAdvice} onChange={v=>setForm({...form,activityAdvice:v})}/>
              <VoiceTextarea label="Warning Signs (return immediately if...)" rows={2} value={form.warningSigns} onChange={v=>setForm({...form,warningSigns:v})}/>
              <VoiceTextarea label="Follow-up Instructions" rows={2} value={form.followUpInstructions} onChange={v=>setForm({...form,followUpInstructions:v})}/>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Follow-up Date</label>
                <input type="date" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.followUpDate} onChange={e=>setForm({...form,followUpDate:e.target.value})}
                  min={new Date().toISOString().split('T')[0]}/>
              </div>
            </>
          )}
        </div>
      </div>

      {!readOnly&&(
        <div className="flex gap-3">
          <button onClick={()=>save(false)} disabled={saving} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 flex-1 py-2.5">{saving?'Saving...':'💾 Save Draft'}</button>
          <button onClick={()=>save(true)} disabled={saving||!form.finalDiagnosis}
            className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 flex-1 py-2.5 font-bold">
            {saving?'Finalizing...':'✅ Finalize & Print'}
          </button>
        </div>
      )}
      {readOnly&&(
        <button onClick={()=>printDischargeSummary(form,patient,user)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 w-full py-2.5">🖨️ Print Summary</button>
      )}
    </div>
  );
}

function ReadField({label,value}:{label:string;value:string}){
  return(
    <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">{label}</p>
      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{value||'—'}</p>
    </div>
  );
}

const VACCINE_ROUTES=['Intramuscular','Subcutaneous','Oral','Intradermal','Intranasal'];

function ImmunizationTab({patient,saving,setSaving,notify}:any){
  const [records,setRecords]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [form,setForm]=useState({
    vaccineName:'',vaccineCode:'',doseNumber:'',totalDoses:'',dateAdministered:new Date().toISOString().slice(0,10),
    nextDueDate:'',site:'',route:'Intramuscular',batchNumber:'',manufacturer:'',adverseReaction:'',notes:'',
  });

  const load=async()=>{
    setLoading(true);
    try{
      const r=await api.getImmunizations(patient.id);
      setRecords(Array.isArray(r.data)?r.data:[]);
    }catch{}
    finally{setLoading(false);}
  };
  useEffect(()=>{ if(patient?.id) load(); },[patient?.id]);

  const save=async()=>{
    if(!form.vaccineName||!form.dateAdministered){notify('⚠️ Vaccine name and date are required');return;}
    setSaving(true);
    try{
      await api.addImmunization({
        patientId:patient.id,
        ...form,
        doseNumber:form.doseNumber?+form.doseNumber:null,
        totalDoses:form.totalDoses?+form.totalDoses:null,
        nextDueDate:form.nextDueDate||null,
      });
      notify('✅ Immunization recorded!');
      setForm({vaccineName:'',vaccineCode:'',doseNumber:'',totalDoses:'',dateAdministered:new Date().toISOString().slice(0,10),nextDueDate:'',site:'',route:'Intramuscular',batchNumber:'',manufacturer:'',adverseReaction:'',notes:''});
      load();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const remove=async(id:string)=>{
    setSaving(true);
    try{ await api.deleteImmunization(id); notify('✅ Removed'); load(); }
    catch{notify('❌ Error');}
    finally{setSaving(false);}
  };

  return(
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="card p-5 rounded-2xl lg:col-span-1">
        <h3 className="font-semibold text-slate-700 mb-3">💉 Record Immunization</h3>
        <div className="space-y-2.5">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Vaccine Name *</label>
            <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.vaccineName} onChange={e=>setForm({...form,vaccineName:e.target.value})} placeholder="e.g. Covishield, Hepatitis B, Tetanus"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Dose #</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" type="number" value={form.doseNumber} onChange={e=>setForm({...form,doseNumber:e.target.value})}/></div>
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Total Doses</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" type="number" value={form.totalDoses} onChange={e=>setForm({...form,totalDoses:e.target.value})}/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Date Administered *</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" type="date" value={form.dateAdministered} onChange={e=>setForm({...form,dateAdministered:e.target.value})}/></div>
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Next Due Date</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" type="date" value={form.nextDueDate} onChange={e=>setForm({...form,nextDueDate:e.target.value})}/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Site</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.site} onChange={e=>setForm({...form,site:e.target.value})} placeholder="e.g. Left deltoid"/></div>
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Route</label>
              <select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.route} onChange={e=>setForm({...form,route:e.target.value})}>
                {VACCINE_ROUTES.map(r=><option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Batch Number</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.batchNumber} onChange={e=>setForm({...form,batchNumber:e.target.value})}/></div>
            <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Manufacturer</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.manufacturer} onChange={e=>setForm({...form,manufacturer:e.target.value})}/></div>
          </div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Adverse Reaction</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.adverseReaction} onChange={e=>setForm({...form,adverseReaction:e.target.value})} placeholder="None / describe"/></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Notes</label><textarea className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></div>
          <button onClick={save} disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 w-full">{saving?'Saving...':'💉 Record Immunization'}</button>
        </div>
      </div>

      <div className="card p-5 rounded-2xl lg:col-span-2">
        <h3 className="font-semibold text-slate-700 mb-3">Immunization History</h3>
        {loading?(
          <div className="py-8 text-center"><div className="w-6 h-6 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
        ):records.length===0?(
          <p className="text-sm text-slate-400 text-center py-8">No immunization records yet</p>
        ):(
          <div className="space-y-2">
            {records.map((r:any)=>(
              <div key={r.id} className="flex items-start justify-between p-3 rounded-lg border border-slate-200">
                <div>
                  <p className="font-semibold text-sm text-slate-800">{r.vaccineName}{r.doseNumber?` — Dose ${r.doseNumber}${r.totalDoses?`/${r.totalDoses}`:''}`:''}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Given: {new Date(r.dateAdministered).toLocaleDateString()}
                    {r.nextDueDate&&` · Next due: ${new Date(r.nextDueDate).toLocaleDateString()}`}
                    {r.site&&` · Site: ${r.site}`}
                    {r.route&&` · Route: ${r.route}`}
                  </p>
                  {(r.batchNumber||r.manufacturer)&&<p className="text-xs text-slate-400">{r.manufacturer}{r.batchNumber&&` · Batch ${r.batchNumber}`}</p>}
                  {r.adverseReaction&&<p className="text-xs text-red-500 mt-1">⚠️ {r.adverseReaction}</p>}
                  {r.notes&&<p className="text-xs text-slate-400 mt-1">{r.notes}</p>}
                </div>
                <button onClick={()=>remove(r.id)} className="text-red-400 hover:text-red-600 text-xs flex-shrink-0">Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DiagnosesTab({patient}:any){
  const [diagnoses,setDiagnoses]=React.useState<any[]>([]);
  const [loading,setLoading]=React.useState(false);
  const [showForm,setShowForm]=React.useState(false);
  const [saving,setSaving]=React.useState(false);
  const [icdSuggestions,setIcdSuggestions]=React.useState<any[]>([]);
  const [form,setForm]=React.useState({diagnosisName:'',icd10Code:'',icd10Description:'',diagnosisType:'PROVISIONAL',isChronic:false,status:'ACTIVE',notes:''});
  const pid=patient?.id||'';

  React.useEffect(()=>{if(!pid)return;setLoading(true);api.getDiagnoses(pid).then(r=>setDiagnoses(r.data||[])).catch(()=>{}).finally(()=>setLoading(false));},[pid]);

  const searchIcd=async(q:string)=>{if(q.length<2){setIcdSuggestions([]);return;}const r=await api.searchIcd(q);setIcdSuggestions(r.data||[]);};

  const save=async(e:React.FormEvent)=>{e.preventDefault();if(!pid)return;setSaving(true);try{await api.saveDiagnosis({...form,patientId:pid});const r=await api.getDiagnoses(pid);setDiagnoses(r.data||[]);setShowForm(false);setForm({diagnosisName:'',icd10Code:'',icd10Description:'',diagnosisType:'PROVISIONAL',isChronic:false,status:'ACTIVE',notes:''});setIcdSuggestions([]);}catch{}finally{setSaving(false);}};

  const TYPE_COLOR:Record<string,string>={PROVISIONAL:'bg-amber-100 text-amber-700',FINAL:'bg-green-100 text-green-700',DIFFERENTIAL:'bg-blue-100 text-[#02357A]',WORKING:'bg-slate-100 text-slate-600'};

  return(
    <div className="space-y-4">
      <div className="flex justify-between items-center"><h3 className="font-semibold text-slate-700">Diagnoses (ICD-10)</h3><button onClick={()=>setShowForm(!showForm)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-xs py-1.5">+ Add Diagnosis</button></div>
      {showForm&&<form onSubmit={save} className="card p-5 rounded-2xl space-y-4 border-2 border-blue-200">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 relative">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">ICD-10 Search / Diagnosis Name *</label>
            <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.diagnosisName} onChange={e=>{setForm({...form,diagnosisName:e.target.value});searchIcd(e.target.value);}} placeholder="Type diagnosis or ICD-10 code..." required/>
            {icdSuggestions.length>0&&<div className="absolute z-10 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">{icdSuggestions.map((s:any)=><button key={s.code} type="button" onClick={()=>{setForm({...form,diagnosisName:s.description,icd10Code:s.code,icd10Description:s.description});setIcdSuggestions([]);}} className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50"><span className="font-mono font-semibold text-[#02357A]">{s.code}</span> — {s.description}</button>)}</div>}
          </div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">ICD-10 Code</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.icd10Code} onChange={e=>setForm({...form,icd10Code:e.target.value})} placeholder="e.g. E11.9"/></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Type</label><select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.diagnosisType} onChange={e=>setForm({...form,diagnosisType:e.target.value})}>{['PROVISIONAL','FINAL','DIFFERENTIAL','WORKING'].map(t=><option key={t}>{t}</option>)}</select></div>
        </div>
        <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Notes</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Additional notes"/></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isChronic} onChange={e=>setForm({...form,isChronic:e.target.checked})} className="w-4 h-4"/><span>Add to chronic conditions registry</span></label>
        <div className="flex gap-2"><button type="submit" disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">{saving?'Saving...':'Save Diagnosis'}</button><button type="button" onClick={()=>setShowForm(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-sm">Cancel</button></div>
      </form>}
      {loading?<div className="py-8 text-center"><div className="w-6 h-6 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>:diagnoses.length===0?<p className="text-sm text-slate-400 text-center py-8">No diagnoses recorded yet</p>:(
        <div className="space-y-2">
          {diagnoses.map((d:any)=>(
            <div key={d.id} className="p-3 rounded-lg border border-slate-200 flex items-start justify-between">
              <div><div className="flex items-center gap-2 mb-1"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TYPE_COLOR[d.diagnosisType]||'bg-slate-100'}`}>{d.diagnosisType}</span>{d.icd10Code&&<span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{d.icd10Code}</span>}</div><p className="font-medium text-slate-700">{d.diagnosisName}</p>{d.notes&&<p className="text-xs text-slate-400 mt-0.5">{d.notes}</p>}</div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ml-2 ${d.status==='ACTIVE'?'bg-green-100 text-green-700':d.status==='RESOLVED'?'bg-slate-100 text-slate-500':'bg-blue-100 text-[#02357A]'}`}>{d.status}</span>
            </div>
          ))}
        </div>
      )}
      </div>
  );
}

function ChronicConditionsTab({patient}:any){
  const [conditions,setConditions]=React.useState<any[]>([]);
  const [showForm,setShowForm]=React.useState(false);
  const [saving,setSaving]=React.useState(false);
  const [form,setForm]=React.useState({conditionName:'',icd10Code:'',onsetDate:'',status:'ACTIVE',monitoringNotes:''});
  const pid=patient?.id||'';
  React.useEffect(()=>{if(!pid)return;api.getChronicConditions(pid).then(r=>setConditions(r.data||[])).catch(()=>{});},[pid]);
  const save=async(e:React.FormEvent)=>{e.preventDefault();if(!pid)return;setSaving(true);try{await api.addChronicCondition({...form,patientId:pid});const r=await api.getChronicConditions(pid);setConditions(r.data||[]);setShowForm(false);setForm({conditionName:'',icd10Code:'',onsetDate:'',status:'ACTIVE',monitoringNotes:''});}catch{}finally{setSaving(false);}};
  return(
    <div className="space-y-4">
      <div className="flex justify-between items-center"><h3 className="font-semibold text-slate-700">Chronic Conditions Registry</h3><button onClick={()=>setShowForm(!showForm)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-xs py-1.5">+ Add Condition</button></div>
      {showForm&&<form onSubmit={save} className="card p-5 rounded-2xl space-y-4 border-2 border-amber-200">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Condition Name *</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.conditionName} onChange={e=>setForm({...form,conditionName:e.target.value})} required placeholder="e.g. Type 2 Diabetes Mellitus"/></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">ICD-10 Code</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.icd10Code} onChange={e=>setForm({...form,icd10Code:e.target.value})} placeholder="e.g. E11.9"/></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Onset Date</label><input type="date" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.onsetDate} onChange={e=>setForm({...form,onsetDate:e.target.value})}/></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Status</label><select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>{['ACTIVE','CONTROLLED','REMISSION','RESOLVED'].map(s=><option key={s}>{s}</option>)}</select></div>
        </div>
        <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Monitoring Notes</label><textarea className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" rows={2} value={form.monitoringNotes} onChange={e=>setForm({...form,monitoringNotes:e.target.value})} placeholder="HbA1c targets, BP targets, medications..."/></div>
        <div className="flex gap-2"><button type="submit" disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">{saving?'Saving...':'Add Condition'}</button><button type="button" onClick={()=>setShowForm(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-sm">Cancel</button></div>
      </form>}
      {conditions.length===0?<p className="text-sm text-slate-400 text-center py-8">No chronic conditions recorded</p>:(
        <div className="space-y-2">
          {conditions.map((c:any)=>(
            <div key={c.id} className="p-3 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium text-slate-700">{c.conditionName}{c.icd10Code&&<span className="ml-2 font-mono text-xs text-[#02357A]">{c.icd10Code}</span>}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.status==='ACTIVE'?'bg-red-100 text-red-700':c.status==='CONTROLLED'?'bg-green-100 text-green-700':c.status==='REMISSION'?'bg-blue-100 text-[#02357A]':'bg-slate-100 text-slate-500'}`}>{c.status}</span>
              </div>
              {c.onsetDate&&<p className="text-xs text-slate-400">Since: {c.onsetDate.slice?c.onsetDate.slice(0,10):c.onsetDate}</p>}
              {c.monitoringNotes&&<p className="text-xs text-slate-500 mt-1">{c.monitoringNotes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CarePlansTab({patient}:any){
  const [plans,setPlans]=React.useState<any[]>([]);
  const [showForm,setShowForm]=React.useState(false);
  const [saving,setSaving]=React.useState(false);
  const [form,setForm]=React.useState({goal:'',treatment:'',reviewDate:'',notes:''});
  const pid=patient?.id||'';
  React.useEffect(()=>{if(!pid)return;api.getCarePlans(pid).then(r=>setPlans(r.data||[])).catch(()=>{});},[pid]);
  const save=async(e:React.FormEvent)=>{e.preventDefault();if(!pid)return;setSaving(true);try{await api.saveCarePlan({...form,patientId:pid});const r=await api.getCarePlans(pid);setPlans(r.data||[]);setShowForm(false);setForm({goal:'',treatment:'',reviewDate:'',notes:''});}catch{}finally{setSaving(false);}};
  const updateStatus=async(id:string,status:string)=>{await api.updateCarePlan(id,{status});const r=await api.getCarePlans(pid);setPlans(r.data||[]);};
  return(
    <div className="space-y-4">
      <div className="flex justify-between items-center"><h3 className="font-semibold text-slate-700">Care Plans</h3><button onClick={()=>setShowForm(!showForm)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-xs py-1.5">+ Add Care Plan</button></div>
      {showForm&&<form onSubmit={save} className="card p-5 rounded-2xl space-y-4 border-2 border-blue-200">
        <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Goal *</label><textarea className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" rows={2} value={form.goal} onChange={e=>setForm({...form,goal:e.target.value})} required placeholder="e.g. Achieve HbA1c < 7% in 3 months"/></div>
        <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Treatment Plan</label><textarea className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" rows={3} value={form.treatment} onChange={e=>setForm({...form,treatment:e.target.value})} placeholder="Medications, lifestyle changes, referrals..."/></div>
        <div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Review Date</label><input type="date" className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.reviewDate} onChange={e=>setForm({...form,reviewDate:e.target.value})}/></div><div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Notes</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></div></div>
        <div className="flex gap-2"><button type="submit" disabled={saving} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-sm">{saving?'Saving...':'Add Care Plan'}</button><button type="button" onClick={()=>setShowForm(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-sm">Cancel</button></div>
      </form>}
      {plans.length===0?<p className="text-sm text-slate-400 text-center py-8">No care plans yet</p>:(
        <div className="space-y-3">
          {plans.map((p:any)=>(
            <div key={p.id} className="card p-5 rounded-2xl">
              <div className="flex justify-between items-start mb-2">
                <p className="font-semibold text-slate-700">{p.goal}</p>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.status==='ACTIVE'?'bg-green-100 text-green-700':p.status==='COMPLETED'?'bg-slate-100 text-slate-500':'bg-red-100 text-red-600'}`}>{p.status}</span>
                  {p.status==='ACTIVE'&&<button onClick={()=>updateStatus(p.id,'COMPLETED')} className="text-xs text-green-600 hover:underline">Complete</button>}
                </div>
              </div>
              {p.treatment&&<p className="text-sm text-slate-600 mb-1">{p.treatment}</p>}
              {p.reviewDate&&<p className="text-xs text-[#02357A]">Review: {p.reviewDate.slice?p.reviewDate.slice(0,10):p.reviewDate}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClinicalAlertsTab({patient}:any){
  const [alerts,setAlerts]=React.useState<any[]>([]);
  const [showForm,setShowForm]=React.useState(false);
  const [saving,setSaving]=React.useState(false);
  const [form,setForm]=React.useState({alertType:'DRUG_ALLERGY',severity:'HIGH',title:'',description:'',triggerItem:''});
  const pid=patient?.id||'';
  React.useEffect(()=>{if(!pid)return;api.getClinicalAlerts(pid).then(r=>setAlerts(r.data||[])).catch(()=>{});},[pid]);
  const save=async(e:React.FormEvent)=>{e.preventDefault();if(!pid)return;setSaving(true);try{await api.createClinicalAlert({...form,patientId:pid});const r=await api.getClinicalAlerts(pid);setAlerts(r.data||[]);setShowForm(false);setForm({alertType:'DRUG_ALLERGY',severity:'HIGH',title:'',description:'',triggerItem:''});}catch{}finally{setSaving(false);}};
  const ack=async(id:string)=>{await api.acknowledgeClinicalAlert(id);const r=await api.getClinicalAlerts(pid);setAlerts(r.data||[]);};
  const SEV_COLOR:Record<string,string>={LOW:'bg-blue-50 border-blue-400 text-[#02357A]',MEDIUM:'bg-amber-50 border-amber-400 text-amber-700',HIGH:'bg-orange-50 border-orange-400 text-orange-700',CRITICAL:'bg-red-50 border-red-500 text-red-700'};
  return(
    <div className="space-y-4">
      <div className="flex justify-between items-center"><h3 className="font-semibold text-slate-700">Clinical Alerts</h3><button onClick={()=>setShowForm(!showForm)} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 text-xs py-1.5">+ Add Alert</button></div>
      {showForm&&<form onSubmit={save} className="card p-5 rounded-2xl space-y-4 border-2 border-red-200">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Alert Type *</label><select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.alertType} onChange={e=>setForm({...form,alertType:e.target.value})}>{['DRUG_ALLERGY','DRUG_INTERACTION','CRITICAL_VALUE','HIGH_RISK','CUSTOM'].map(t=><option key={t}>{t}</option>)}</select></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Severity</label><select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.severity} onChange={e=>setForm({...form,severity:e.target.value})}>{['LOW','MEDIUM','HIGH','CRITICAL'].map(s=><option key={s}>{s}</option>)}</select></div>
          <div className="col-span-2"><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Title *</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} required placeholder="e.g. Drug Allergy: Penicillin — Anaphylaxis"/></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Trigger Item</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.triggerItem} onChange={e=>setForm({...form,triggerItem:e.target.value})} placeholder="Drug/test name"/></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Description</label><input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
        </div>
        <div className="flex gap-2"><button type="submit" disabled={saving} className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-xl transition-all text-sm shadow-md disabled:opacity-50 text-sm">{saving?'Saving...':'Create Alert'}</button><button type="button" onClick={()=>setShowForm(false)} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-sm">Cancel</button></div>
      </form>}
      {alerts.length===0?<p className="text-sm text-slate-400 text-center py-8">No active clinical alerts</p>:(
        <div className="space-y-2">
          {alerts.map((a:any)=>(
            <div key={a.id} className={`p-3 rounded-lg border-l-4 ${SEV_COLOR[a.severity]||'bg-slate-50 border-slate-400 text-slate-700'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1"><span className="font-bold text-sm">⚠️ {a.title}</span><span className={`text-xs px-1.5 rounded font-semibold ${SEV_COLOR[a.severity]}`}>{a.severity}</span></div>
                  <p className="text-xs opacity-80">{a.alertType.replace('_',' ')} {a.triggerItem&&`· ${a.triggerItem}`}</p>
                  {a.description&&<p className="text-xs opacity-70 mt-0.5">{a.description}</p>}
                </div>
                {a.isActive&&<button onClick={()=>ack(a.id)} className="text-xs border rounded px-2 py-1 hover:bg-white/50 flex-shrink-0 ml-2">Acknowledge</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvestigationResultsTab({patient}:any){
  const [labResults,setLabResults]=React.useState<any[]>([]);
  const [radResults,setRadResults]=React.useState<any[]>([]);
  const [loading,setLoading]=React.useState(false);
  const [activeType,setActiveType]=React.useState<'lab'|'radiology'>('lab');

  React.useEffect(()=>{
    if(!patient?.id)return;
    setLoading(true);
    Promise.all([
      api.getLabRequests({patientId:patient.id,take:20}).then(r=>setLabResults(r.data?.data||(Array.isArray(r.data)?r.data:[]))),
      api.getRadiologyOrders({patientId:patient.id,take:20}).then(r=>setRadResults(r.data?.orders||r.data?.data||(Array.isArray(r.data)?r.data:[]))),
    ]).catch(()=>{}).finally(()=>setLoading(false));
  },[patient?.id]);

  const STATUS_COLOR:Record<string,string>={
    COMPLETED:'bg-green-100 text-green-700',ORDERED:'bg-blue-100 text-[#02357A]',
    SAMPLE_COLLECTED:'bg-yellow-100 text-yellow-700',PROCESSING:'bg-orange-100 text-orange-700',
    CANCELLED:'bg-red-100 text-red-700',
  };

  return(
    <div className="space-y-4">
      <div className="flex gap-2 border-b pb-2">
        <button onClick={()=>setActiveType('lab')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${activeType==='lab'?'bg-[#02357A] text-white':'text-slate-600 hover:bg-slate-100'}`}>🔬 Lab Reports ({labResults.length})</button>
        <button onClick={()=>setActiveType('radiology')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${activeType==='radiology'?'bg-purple-600 text-white':'text-slate-600 hover:bg-slate-100'}`}>🩻 Radiology ({radResults.length})</button>
      </div>

      {loading&&<p className="text-slate-400 text-sm text-center py-8">Loading results...</p>}

      {!loading&&activeType==='lab'&&(
        <div className="space-y-3">
          {labResults.length===0&&<p className="text-slate-400 text-sm text-center py-8">No lab investigations ordered yet</p>}
          {labResults.map((req:any)=>(
            <div key={req.id} className="card p-5 rounded-2xl">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-mono text-xs text-[#02357A]">{req.request_number||req.requestNumber}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLOR[req.status]||'bg-slate-100'}`}>{req.status}</span>
                </div>
                <span className="text-xs text-slate-400">{fmtDate(req.created_at||req.createdAt)}</span>
              </div>
              {(()=>{
                let tests:any[]=[];
                try{tests=typeof req.tests==='string'?JSON.parse(req.tests):req.tests||[];}catch{}
                return tests.length>0&&<p className="text-sm text-slate-700 mb-2"><span className="text-slate-400 text-xs">Tests: </span>{tests.map((t:any)=>t.name||t.testName).join(', ')}</p>;
              })()}
              {req.status==='COMPLETED'&&(
                <ResultValuesSection requestId={req.id}/>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading&&activeType==='radiology'&&(
        <div className="space-y-3">
          {radResults.length===0&&<p className="text-slate-400 text-sm text-center py-8">No radiology investigations ordered yet</p>}
          {radResults.map((r:any)=>(
            <div key={r.id} className="card p-5 rounded-2xl">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <span className="font-mono text-xs text-purple-600">{r.order_number||r.orderNumber}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLOR[r.status]||'bg-slate-100'}`}>{r.status}</span>
                </div>
                <span className="text-xs text-slate-400">{fmtDate(r.created_at||r.createdAt)}</span>
              </div>
              {(()=>{let tests:any[]=[];try{tests=typeof r.tests==='string'?JSON.parse(r.tests):r.tests||[];}catch{}return tests.length>0&&<p className="text-sm text-slate-700">{tests.map((t:any)=>t.name||t.testName).join(', ')}</p>;})()}
              {r.radiologist_remarks||r.report_text||r.findings?(
                <div className="mt-2 bg-purple-50 rounded p-2 text-xs text-slate-700">
                  {r.findings&&<p><span className="font-semibold">Findings: </span>{r.findings}</p>}
                  {r.impression&&<p className="mt-1"><span className="font-semibold">Impression: </span>{r.impression}</p>}
                  {r.radiologist_remarks&&<p className="mt-1 text-slate-500">{r.radiologist_remarks}</p>}
                </div>
              ):r.status==='COMPLETED'&&<p className="text-xs text-green-600 mt-1">Report available — view in Radiology module</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultValuesSection({requestId}:{requestId:string}){
  const [results,setResults]=React.useState<any[]>([]);
  React.useEffect(()=>{
    api.getLabResults(requestId).then(r=>setResults(r.data||[])).catch(()=>{});
  },[requestId]);
  if(!results.length)return null;
  return(
    <div className="mt-2">
      {results.map((res:any)=>{
        let params:any[]=[];
        try{params=typeof res.parameters==='string'?JSON.parse(res.parameters):res.parameters||[];}catch{}
        return(
          <div key={res.id} className="bg-slate-50 rounded-lg p-3 text-xs">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-1.5 py-0.5 rounded font-semibold ${res.status==='VERIFIED'?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700'}`}>{res.status}</span>
              {res.is_critical&&<span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">⚠️ CRITICAL</span>}
            </div>
            {params.length>0?(
              <table className="w-full text-xs">
                <thead><tr className="border-b"><th className="text-left py-1 text-slate-500">Parameter</th><th className="text-right py-1 text-slate-500">Value</th><th className="text-right py-1 text-slate-500">Unit</th><th className="text-right py-1 text-slate-500">Range</th><th className="text-right py-1 text-slate-500">Status</th></tr></thead>
                <tbody>
                  {params.map((p:any,i:number)=>{
                    const isAbn=p.isAbnormal||p.is_abnormal||p.status==='HIGH'||p.status==='LOW';
                    const isCrit=p.isCritical||p.is_critical||p.status?.includes('CRITICAL');
                    return(
                      <tr key={i} className={`border-b ${isCrit?'bg-red-50':isAbn?'bg-yellow-50':''}`}>
                        <td className="py-1 font-medium">{p.parameterName||p.parameter_name||p.name}</td>
                        <td className={`py-1 text-right font-bold ${isCrit?'text-red-700':isAbn?'text-orange-600':'text-slate-800'}`}>{p.value}</td>
                        <td className="py-1 text-right text-slate-400">{p.unit}</td>
                        <td className="py-1 text-right text-slate-400">{p.normalRange||p.normal_range||`${p.normalMin||p.normal_min||''}-${p.normalMax||p.normal_max||''}`}</td>
                        <td className="py-1 text-right">
                          {isCrit&&<span className="text-red-700 font-bold">⚠️ CRIT</span>}
                          {!isCrit&&isAbn&&<span className="text-orange-600">↑↓</span>}
                          {!isCrit&&!isAbn&&<span className="text-green-600">✓</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ):(
              <p className="text-slate-600">{res.result_text||res.resultText||'Results recorded'}</p>
            )}
            {res.interpretation&&<p className="mt-2 text-slate-500 italic">{res.interpretation}</p>}
          </div>
        );
      })}
    </div>
  );
}
